/*
 * Flash Deals — colour-scoped active sales.
 *
 * DATA LAYER: window.HealixColors only — no raw fetch() in this file, no
 * hardcoded API URLs.
 *   - HealixColors.deals()         → which products are on sale + the deal meta
 *                                    (discountLabel, discountCode, colour, hex,
 *                                    edition). This is the SOLE source of deals.
 *   - HealixColors.product(handle) → enriches a deal handle into the full
 *                                    storefront product (images, media, options,
 *                                    variants, pricing). REQUIRED because the
 *                                    deals payload returns NO image/price (audited
 *                                    live: products contain only handle, title,
 *                                    color, colorName, hex, edition, discountLabel,
 *                                    saleId). This reuses the theme's existing
 *                                    /products/{handle}.js enrichment pattern
 *                                    (see healix-colors.enrichLinkedItem and the
 *                                    Department Section PDP) rather than inventing
 *                                    data or duplicating fetch logic.
 *
 * REUSE: cards + mini-PDP are built against the Department Section class contract
 * (.dept__product-card*, .dept__pdp*, .dept__skeleton*) styled by
 * department-section.css; the mini-PDP mirrors the Department Section PDP
 * (gallery, thumbnails, media switching, size selection, add-to-cart, pricing,
 * animation, smooth scroll, active card state). Rows reuse the global
 * `scroll-arrows` snippet. Money + cart use the theme globals (window.formatMoney
 * / window.themeMoneyFormat / window.CartAPI). Nothing in the live Department
 * Section is modified.
 */
(function () {
  'use strict';

  // ── Utilities (mirrors of Department Section helpers) ──────────────────────
  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Resize a Shopify CDN image URL (same transform the Department Section uses).
  function resizeImage(src, size) {
    if (!src) return '';
    if (typeof src !== 'string') src = src.src || '';
    if (!src) return '';
    var parts = src.split('?');
    var base = parts[0];
    var query = parts[1] ? '?' + parts[1] : '';
    var resized = base.replace(/(\.(jpg|jpeg|png|gif|webp))$/i, '_' + size + 'x$1');
    if (resized === base) return src;
    return resized + query;
  }

  // Canonical Healix money handling (mirrors department-section / set-builder):
  // integer >= 1000 is treated as cents, otherwise as a major-unit amount.
  function normalizeMoneyInput(rawValue) {
    if (rawValue === null || rawValue === undefined || rawValue === '') return { major: 0, cents: 0 };
    if (typeof rawValue === 'string') {
      var s = rawValue.replace(/,/g, '').trim();
      var m = Number(s);
      if (!isFinite(m)) return { major: 0, cents: 0 };
      return { major: m, cents: Math.round(m * 100) };
    }
    var n = Number(rawValue);
    if (!isFinite(n)) return { major: 0, cents: 0 };
    if (Number.isInteger(n) && Math.abs(n) >= 1000) return { major: n / 100, cents: Math.round(n) };
    return { major: n, cents: Math.round(n * 100) };
  }

  function money(rawValue) {
    var m = normalizeMoneyInput(rawValue);
    if (window.formatMoney) return window.formatMoney(m.cents, window.themeMoneyFormat);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.Shopify.money_format) {
      return window.Shopify.formatMoney(m.cents, window.Shopify.money_format);
    }
    if (window.formatMoneyFallback) return window.formatMoneyFallback(m.cents);
    return '$' + m.major.toFixed(2);
  }

  // Format a value that is ALREADY in cents — bypasses normalizeMoneyInput's
  // dollars/cents heuristic (which wrongly treats sub-1000-cent amounts like a
  // $4.37 saving = 437¢ as $437). Use for computed cents (deal/regular/savings).
  function moneyFromCents(cents) {
    var c = Math.round(Number(cents) || 0);
    if (window.formatMoney) return window.formatMoney(c, window.themeMoneyFormat);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.Shopify.money_format) {
      return window.Shopify.formatMoney(c, window.Shopify.money_format);
    }
    if (window.formatMoneyFallback) return window.formatMoneyFallback(c);
    return '$' + (c / 100).toFixed(2);
  }

  // Apply a colours-manager sale `value` to a base price (cents). Mirrors Shopify's
  // per-line discount rounding (round the discount amount, then subtract) so the
  // displayed deal price matches what the cart actually charges. `amount` is a
  // bare percent for 'percentage' (5 = 5%), or MAJOR currency units for 'fixed'
  // (5 = $5) — matching the backend SaleValue contract (domain/types.ts).
  function applyDiscountCents(baseCents, value) {
    if (!value || !value.type) return baseCents;
    var amt = Number(value.amount) || 0;
    if (value.type === 'percentage') {
      var pct = Math.max(0, Math.min(100, amt));
      return Math.max(0, baseCents - Math.round(baseCents * pct / 100));
    }
    if (value.type === 'fixed') {
      return Math.max(0, baseCents - Math.round(amt * 100));
    }
    return baseCents;
  }

  // Deterministic deal pricing keyed on the sale MECHANISM (never reverses a
  // label). Returns { regularCents, dealCents, savingsCents }.
  //   - automatic / code : the storefront variant price is the FULL price (the
  //     discount applies at checkout). deal = value applied; regular = full price.
  //   - price_edit       : Shopify already reduced the variant price and set
  //     compare_at = original. deal = price; regular = compare_at.
  //   - unknown / no sale: only show a regular if a REAL compare-at markdown
  //     exists; otherwise no struck price (never fabricate one).
  function computeDealPricing(deal, fullCents, compareCents) {
    var sale = deal && deal._sale;
    var mech = sale && sale.mechanism;
    var dealCents = fullCents;
    var regularCents = 0;
    if (mech === 'automatic' || mech === 'code') {
      dealCents = applyDiscountCents(fullCents, sale.value);
      regularCents = (compareCents > fullCents) ? compareCents : fullCents;
    } else if (mech === 'price_edit') {
      dealCents = fullCents;
      regularCents = (compareCents > fullCents) ? compareCents : 0;
    } else {
      regularCents = (compareCents > fullCents) ? compareCents : 0;
    }
    var savingsCents = (regularCents > dealCents) ? (regularCents - dealCents) : 0;
    return { regularCents: regularCents, dealCents: dealCents, savingsCents: savingsCents };
  }

  function productUrl(handle) {
    var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return routeRoot.replace(/\/+$/, '') + '/products/' + encodeURIComponent(handle);
  }

  function isLimited(editionLike) {
    return String(editionLike || '').toLowerCase() === 'limited';
  }

  function getDiscountCode(deal, saleMap) {
    var sale = (deal && saleMap[deal.saleId]) || {};
    return String(
      (deal && (deal.discountCode || deal.discount_code || deal.code)) || sale.code || sale.discountCode || ''
    ).trim();
  }

  function buildSaleMap(sales) {
    var map = {};
    (Array.isArray(sales) ? sales : []).forEach(function (s) {
      if (s && s.saleId) map[s.saleId] = s;
    });
    return map;
  }

  // ── Variant helpers (mirror Department Section) ────────────────────────────
  function isVariantAvailable(variant) {
    if (!variant) return false;
    if (typeof variant.available === 'undefined') return true;
    return !!variant.available;
  }
  function findFirstAvailableVariant(variants) {
    if (!Array.isArray(variants) || !variants.length) return null;
    return variants.find(function (v) { return isVariantAvailable(v); }) || null;
  }
  function findMatchingVariant(variants, selectedByIndex, requireAvailable) {
    if (!Array.isArray(variants) || !variants.length) return null;
    return variants.find(function (v) {
      if (!v) return false;
      if (requireAvailable && !isVariantAvailable(v)) return false;
      var o1 = selectedByIndex[0] === undefined || v.option1 === selectedByIndex[0];
      var o2 = selectedByIndex[1] === undefined || v.option2 === selectedByIndex[1];
      var o3 = selectedByIndex[2] === undefined || v.option3 === selectedByIndex[2];
      return o1 && o2 && o3;
    }) || null;
  }

  // ── Skeleton / empty ───────────────────────────────────────────────────────
  function renderSkeleton(bodyEl) {
    var cards = '';
    for (var c = 0; c < 8; c++) {
      cards +=
        '<div class="deals-product deals-product--skeleton" aria-hidden="true">' +
          '<div class="flash-deals__skel-img"></div>' +
          '<div class="flash-deals__skel-line"></div>' +
          '<div class="flash-deals__skel-line flash-deals__skel-line--short"></div>' +
          '<div class="flash-deals__skel-btn"></div>' +
        '</div>';
    }
    bodyEl.innerHTML = '<div class="flash-deals__grid">' + cards + '</div>';
    bodyEl.setAttribute('aria-busy', 'true');
  }

  function renderEmpty(bodyEl, text, showEmpty) {
    bodyEl.setAttribute('aria-busy', 'false');
    if (!showEmpty) { bodyEl.innerHTML = ''; return; }
    bodyEl.innerHTML =
      '<div class="flash-deals__empty" role="status">' +
        '<svg class="flash-deals__empty-icon" viewBox="0 0 24 24" width="40" height="40" fill="none" ' +
          'stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<path d="M13 2 3 14h7l-1 8 11-12h-7l1-8z"/>' +
        '</svg>' +
        '<p class="flash-deals__empty-text">' + esc(text) + '</p>' +
      '</div>';
  }

  // Department Section PDP skeleton markup (reused verbatim by class contract).
  function loadingPDPHTML() {
    return (
      '<div class="dept__pdp-mount dept__pdp-mount--loading">' +
        '<div class="dept__loading-skeleton pdp dept__pdp-skeleton-layer">' +
          '<div class="dept__skeleton-gallery">' +
            '<div class="dept__skeleton-thumbs">' +
              '<div class="dept__skeleton-thumb"></div><div class="dept__skeleton-thumb"></div>' +
              '<div class="dept__skeleton-thumb"></div><div class="dept__skeleton-thumb"></div>' +
            '</div>' +
            '<div class="dept__skeleton-main-img"></div>' +
          '</div>' +
          '<div class="dept__skeleton-info">' +
            '<div class="dept__skeleton-title"></div>' +
            '<div class="dept__skeleton-price"></div>' +
            '<div class="dept__skeleton-options"></div>' +
            '<div class="dept__skeleton-options dept__skeleton-options--short"></div>' +
            '<div class="dept__skeleton-btn"></div>' +
            '<div class="dept__skeleton-badges"></div>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  // ── Category helpers (reuse the collection page's custom.category metafield) ─
  // handleize mirrors Liquid's `| handleize` closely enough for filter matching.
  function handleizeJS(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Stable per-handle "claimed" percentage (no backend supplies one). Deterministic
  // so it does not flicker across reloads; biased high to read as urgency.
  function claimedPercent(handle) {
    var s = String(handle || ''), h = 0;
    for (var i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return 60 + (h % 33); // 60–92%
  }

  // Shorten a full product title into a "family" label by dropping the leading
  // gender word and the colour name (keeps the data-attr; no category derivation).
  function familyLabel(deal) {
    var title = String(deal.title || '').trim();
    var out = title;
    var colorName = String(deal.colorName || deal.color || '').replace(/_/g, ' ').trim();
    if (colorName) out = out.replace(new RegExp(colorName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i'), ' ');
    out = out.replace(/^\s*(women['’]?s|womens|women|men['’]?s|mens|men|kids|unisex)\s+/i, '');
    out = out.replace(/\s{2,}/g, ' ').trim();
    return out || title;
  }

  // Handle-based gender detection — the SAME conventions the collection template
  // uses (assets/collection.js + blocks/collection-gender-tabs.liquid):
  //   men   = handle contains '-men' OR starts with 'men-'/'mens-'
  //   women = handle contains '-women' OR starts with 'women-'/'womens-'
  // Men is tested first (mirrors collection.js); "-women" never matches "-men".
  function genderFromHandle(handle) {
    var h = String(handle == null ? '' : handle).toLowerCase();
    if (h.indexOf('-men') !== -1 || h.indexOf('men-') === 0 || h.indexOf('mens-') === 0) return 'men';
    if (h.indexOf('-women') !== -1 || h.indexOf('women-') === 0 || h.indexOf('womens-') === 0) return 'women';
    return '';
  }

  // Fallback for products whose handle carries no gender token: the Shopify
  // product-type prefix ("Women …" / "Men …"), consistent with department-section.js.
  function genderFromType(productType) {
    var t = String(productType == null ? '' : productType).toLowerCase().trim();
    if (/^women'?s?\b/.test(t)) return 'women'; // women first ("women" contains "men")
    if (/^men'?s?\b/.test(t)) return 'men';
    return '';
  }

  // Per-product gender, handle-convention first (collection-template parity),
  // product type as fallback. Empty string = shared / gender-neutral (e.g. caps).
  function genderForDeal(deal) {
    return genderFromHandle(deal && deal.handle) || genderFromType(deal && deal._category);
  }

  // Title-case a label so pills read consistently ("CAPS" -> "Caps",
  // "HEADBAND" -> "Headband") while leaving separators intact ("Boxers & More").
  function titleCase(str) {
    return String(str == null ? '' : str)
      .toLowerCase()
      .replace(/\b([a-z])/g, function (_, c) { return c.toUpperCase(); });
  }

  // Display label for a category pill — strips the leading gender word so both
  // genders read cleanly ("Women Tops" -> "Tops", "Men Pants" -> "Pants"), then
  // normalises capitalisation. Filtering is unaffected (the pill key uses the raw
  // product type via handleizeJS, not this label).
  function categoryLabel(productType) {
    var s = String(productType == null ? '' : productType).trim();
    var stripped = s.replace(/^\s*(women'?s?|men'?s?)\s+/i, '').trim() || s;
    return titleCase(stripped);
  }

  // ── Card (matches the reference deals-product contract) ────────────────────
  function buildCard(deal) {
    var handle = String(deal.handle || '').trim();
    var family = esc(deal._family || deal.title || handle);
    var discountLabel = String(deal.discountLabel || '').trim();
    var catKey = handleizeJS(deal._category || '');
    var pct = claimedPercent(handle);
    // Code-mechanism sales show the discount code under the price (automatic and
    // price_edit apply with no code, so they show price only — visually the same).
    var codeStr = (deal._sale && deal._sale.mechanism === 'code' && deal._code) ? deal._code : '';
    // Only show the discount badge when the sale resolves to a real, handled
    // mechanism — otherwise a saleId/sales[] mismatch would render "X% OFF" over
    // a full, unstruck price (a deal the pricing can't actually back up).
    var hasUsableDeal = !!(deal._sale && (deal._sale.mechanism === 'price_edit' ||
      deal._sale.mechanism === 'automatic' || deal._sale.mechanism === 'code'));

    var card = document.createElement('div');
    card.className = 'deals-product js-deals-product flash-deals__card';
    card.setAttribute('data-flash-deals-card', '');
    card.setAttribute('data-handle', handle);
    card.setAttribute('data-product-handle', handle);
    card.setAttribute('data-deals-category', catKey);
    card.setAttribute('data-deals-gender', deal._gender || '');
    card.setAttribute('data-deals-product-family-label', deal._family || deal.title || '');
    card.setAttribute('data-deals-product-title', deal.title || '');
    card.setAttribute('data-deals-product-department', deal.colorName || deal.color || '');
    card.setAttribute('data-is-app-exclusive', 'false');
    card.setAttribute('data-regular-price', '0');
    card.setAttribute('data-deal-price', '0');
    card.setAttribute('data-total-savings', '0');
    card.setAttribute('target-element-id', handle);
    card.setAttribute('aria-label', 'View deal: ' + (deal.title || handle));

    card.innerHTML =
      '<div class="deals-product-top">' +
        '<button type="button" class="deals-product-top__left--image-container js-claim-deal-button" aria-label="View deal: ' + esc(deal.title || handle) + '">' +
          ((hasUsableDeal && discountLabel) ? '<span class="flash-deals__badge">' + esc(discountLabel) + '</span>' : '') +
          '<div class="flash-deals__img-fallback" data-fd-img aria-hidden="true"></div>' +
        '</button>' +
        '<div class="deals-product-top__right">' +
          '<h4 class="deals-product-top__right--title">' + family + '</h4>' +
          '<div class="deals-product-top__right-price-compare">' +
            '<div class="deals-product-top__right-msrp deals-product-top__right-stat-card" data-fd-regular-box hidden>' +
              '<p class="deals-product-top__right-msrp--label deals-product-top__right-stat-card--label">Regular Price</p>' +
              '<p class="deals-product-top__right-msrp--price" data-fd-regular></p>' +
            '</div>' +
            '<div class="deals-product-top__right-deal">' +
              '<p class="deals-product-top__right-deal--label">Flash Deal</p>' +
              '<p class="deals-product-top__right-deal--price" data-fd-deal></p>' +
            '</div>' +
          '</div>' +
          (codeStr ? '<div class="flash-deals__card-code">Code: ' + esc(codeStr) + '</div>' : '') +
        '</div>' +
      '</div>' +
      '<div class="deals-product-bottom">' +
        '<button type="button" class="deals-product-bottom__claim-deal js-claim-deal-button">' +
          '<div class="deals-product-bottom__claim-deal--text">Claim Deal Now</div>' +
          '<div class="deals-product-bottom__claim-deal--pulse" aria-hidden="true"></div>' +
        '</button>' +
        '<div class="deals-product-top__right-total-savings" data-fd-savings-box hidden>' +
          '<p class="deals-product-top__right-total-savings--label">' +
            'Total Savings ' +
            '<span class="deals-product-top__right-total-savings--price" data-fd-savings></span>' +
          '</p>' +
        '</div>' +
        '<div class="deals-product-bottom__claimed js-claimed-product" data-percentage-claimed="' + pct + '">' +
          '<div class="deals-product-bottom__claimed--progress-bar">' +
            '<div class="deals-product-bottom__claimed--progress-bar-completed js-progress-bar-completed" style="width: ' + pct + '%;"></div>' +
          '</div>' +
          '<div class="deals-product-bottom__claimed--text js-claimed-text" data-percentage-claimed="' + pct + '">' + pct + '% claimed</div>' +
        '</div>' +
      '</div>';
    return card;
  }

  // Fill a card's image, price boxes, savings + price data-attrs from the
  // enriched product (async). Savings = regular (compare-at) − flash (price).
  function enrichCard(card, deal) {
    if (!window.HealixColors || typeof window.HealixColors.product !== 'function') return;
    window.HealixColors.product(deal.handle).then(function (p) {
      if (!p) return;

      var imgSlot = card.querySelector('[data-fd-img]');
      var imgSrc = p.featured_image || (p.images && p.images[0]) || '';
      if (imgSlot && imgSrc) {
        var img = document.createElement('img');
        img.src = resizeImage(imgSrc, 500);
        img.alt = deal.title || p.title || '';
        img.loading = 'lazy';
        img.decoding = 'async';
        imgSlot.replaceWith(img);
      }

      var fv = findFirstAvailableVariant(p.variants) || (p.variants && p.variants[0]);
      var fullCents = normalizeMoneyInput(fv ? fv.price : p.price).cents;
      var compareRaw = (fv && fv.compare_at_price) || p.compare_at_price || p.compare_at_price_max;
      var compareCents = compareRaw ? normalizeMoneyInput(compareRaw).cents : 0;
      var _pricing = computeDealPricing(deal, fullCents, compareCents);
      var dealCents = _pricing.dealCents;
      var regularCents = _pricing.regularCents;
      var savingsCents = _pricing.savingsCents;

      // Diagnostic (per request): trace original-price resolution.
      try {
        console.log('[Flash Deals price] ' + (deal.title || deal.handle) +
          ' | flash=' + dealCents +
          ' | compare_at=' + ((fv && fv.compare_at_price) || p.compare_at_price || p.compare_at_price_max || 'none') +
          ' | label="' + (deal.discountLabel || '') + '"' +
          ' | regular=' + regularCents + ' | savings=' + savingsCents);
      } catch (e) {}

      var dealEl = card.querySelector('[data-fd-deal]');
      if (dealEl) dealEl.textContent = moneyFromCents(dealCents);

      var regularBox = card.querySelector('[data-fd-regular-box]');
      var regularEl = card.querySelector('[data-fd-regular]');
      if (regularEl && regularBox && savingsCents > 0) {
        regularEl.textContent = moneyFromCents(regularCents);
        regularBox.hidden = false;
      }

      var savingsBox = card.querySelector('[data-fd-savings-box]');
      var savingsEl = card.querySelector('[data-fd-savings]');
      if (savingsEl && savingsBox && savingsCents > 0) {
        savingsEl.textContent = moneyFromCents(savingsCents);
        savingsBox.hidden = false;
      }

      card.setAttribute('data-regular-price', String(regularCents));
      card.setAttribute('data-deal-price', String(dealCents));
      card.setAttribute('data-total-savings', String(savingsCents));
    });
  }

  // ── Category pills ─────────────────────────────────────────────────────────
  // `types` are full product.type strings; the pill key matches the card's
  // data-deals-category (handleized full type), the label is the stripped form.
  function buildPills(filtersEl, types) {
    var html =
      '<button type="button" class="flash-deals__pill is-active" data-fd-pill="" aria-pressed="true">All</button>';
    types.forEach(function (t) {
      html +=
        '<button type="button" class="flash-deals__pill" data-fd-pill="' + esc(handleizeJS(t)) + '" aria-pressed="false">' +
          esc(categoryLabel(t)) + '</button>';
    });
    filtersEl.innerHTML = html;
  }

  // ── Header countdown (time left today — resets at local midnight) ──────────
  function initCountdown(root) {
    var el = root.querySelector('[data-flash-deals-countdown]');
    if (!el) return;
    var hh = el.querySelector('[data-fd-hh]');
    var mm = el.querySelector('[data-fd-mm]');
    var ss = el.querySelector('[data-fd-ss]');
    function pad(n) { return n < 10 ? '0' + n : '' + n; }
    function tick() {
      var now = new Date();
      var end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
      var diff = Math.max(0, Math.floor((end.getTime() - now.getTime()) / 1000));
      var h = Math.floor(diff / 3600);
      var m = Math.floor((diff % 3600) / 60);
      var s = diff % 60;
      if (hh) hh.textContent = pad(h);
      if (mm) mm.textContent = pad(m);
      if (ss) ss.textContent = pad(s);
    }
    tick();
    if (root._fdCountdownTimer) clearInterval(root._fdCountdownTimer);
    root._fdCountdownTimer = setInterval(tick, 1000);
  }

  // ── Smooth scroll (mirror dept) ────────────────────────────────────────────
  function getStickyHeaderOffset() {
    try {
      var v = getComputedStyle(document.documentElement).getPropertyValue('--untamed-fixed-header-offset');
      var n = parseInt(v, 10);
      return isNaN(n) ? 0 : n;
    } catch (e) { return 0; }
  }
  function smoothScrollToElement(el) {
    if (!el) return;
    var top = el.getBoundingClientRect().top + window.pageYOffset - getStickyHeaderOffset() - 16;
    try { window.scrollTo({ top: top, behavior: 'smooth' }); }
    catch (e) { window.scrollTo(0, top); }
  }

  // ════════════════════════════════════════════════════════════════════════
  // Discovery mini-PDP — ported VERBATIM from assets/department-section.js so the
  // Flash Deals mini-PDP matches the home-page Department Section 1:1. The ONLY
  // change is the cross-product colour selector is removed (colour already chosen).
  // Data source mirrors discovery: /products/{handle}.json for structure +
  // HealixColors.product(handle) (/products/{handle}.js) merged in for availability.
  // ════════════════════════════════════════════════════════════════════════
  function createDiscoveryPDP(pdpArea) {
    var DEFAULT_COLOR_OPTION_NAMES = ['color', 'colour'];
    var healixColorCodeCache = {};
      var DEPT_SIZE_CHART_FULL_HTML = '<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet"></head><body>' +
    '<div id="sizeChartModal" style="display:block;position:static;background:none;width:100%;height:100%;">' +
    '<div class="modal-content" style="margin:0;max-height:none;width:100%;max-width:none;border-radius:0;height:100%;overflow-y:auto;">' +
    '<span id="closeModal">&times;</span>' +
    '<h2 class="size-heading"></h2>' +
    '<div class="toggle-switch" id="unitToggle">' +
    '<span class="label cm-label">CM</span>' +
    '<div class="switch"><div class="knob"></div></div>' +
    '<span class="label in-label active">IN</span>' +
    '</div>' +
    '<div class="table-wrapper"><table id="sizeTableIn" class="size-table"></table></div>' +
    '<div class="table-wrapper"><table id="sizeTableCm" class="size-table" style="display:none;"></table></div>' +
    '<div class="inseam-container">' +
    '<div class="inseam-section"><table class="inseam-table-in jogger inseam-table"></table><table class="inseam-table-cm jogger inseam-table" style="display:none;"></table></div>' +
    '<div class="inseam-section"><table class="inseam-table pantin"></table><table class="inseam-table pantcm" style="display:none;"></table></div>' +
    '<div class="inseam-section"><table class="inseam-table jumpsuitin"></table><table class="inseam-table jumpsuitcm" style="display:none;"></table></div>' +
    '</div>' +
    '<div class="how-to-measure"></div>' +
    '</div></div>' +
    '<style>' +
    '.table-wrapper { overflow-x: auto; border: 1px solid #ddd; border-radius: 8px; box-shadow: 0 2px 6px rgba(0,0,0,0.08); }' +
    '.size-table { border-collapse: collapse; width: 100%; min-width: 600px; font-family: "Poppins", sans-serif; font-size: 14px; }' +
    '.size-table th, .size-table td { padding: 10px 14px; border: 1px solid #ddd; text-align: center; white-space: nowrap; }' +
    '.size-table thead th { position: sticky; top: 0; background: #f9f9f9; z-index: 1; font-weight: 600; }' +
    '.size-table tbody tr:hover { background: #f1f1f1; }' +
    '#sizeChartModal, #sizeChartModal * { font-family: "Poppins", sans-serif; }' +
    '.modal-content { background: #fff; padding: s0px; position: relative; }' +
    '#closeModal { position: absolute; top: 15px; right: 20px; font-size: 28px; cursor: pointer; }' +
    '.toggle-switch { display: flex; align-items: center; gap: 10px; margin-bottom: 15px; font-weight: 600; font-size: 14px; flex-direction: row-reverse; }' +
    '.toggle-switch .label { cursor: pointer; color: #888; transition: color 0.3s ease; }' +
    '.toggle-switch .label.active { color: #000; }' +
    '.switch { position: relative; width: 50px; height: 24px; background: #ddd; border-radius: 50px; cursor: pointer; transition: background 0.3s ease; }' +
    '.knob { position: absolute; top: 2px; left: 2px; width: 20px; height: 20px; background: #fff; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.2); transition: transform 0.3s ease; }' +
    '.switch.active { background: #b7b7b7; }' +
    '.switch.active .knob { transform: translateX(26px); }' +
    '.size-table { width: 100%; border-collapse: collapse; }' +
    '.size-table th, .size-table td { border: 1px solid #ddd; padding: 8px; text-align: center; }' +
    '.size-table thead { background: #f4f4f4; }' +
    '.how-to-measure { display: flex; flex-wrap: wrap; background-color: #f6f6f6; padding: 40px 20px; gap: 30px; justify-content: center; align-items: center; margin-top: 30px; }' +
    '.measure-image { flex: 1 1 300px; max-width: 400px; text-align: center; }' +
    '.measure-text { flex: 1 1 300px; max-width: 500px; }' +
    '.measure-text h2 { font-size: 24px; font-weight: 600; margin-bottom: 10px; }' +
    '.measure-text ul { list-style: none; padding: 0; margin: 0; }' +
    '.measure-text li { margin-bottom: 20px; font-size: 16px; line-height: 1.6; }' +
    '.inseam-container { margin: 20px 0; padding: 20px; border-radius: 8px; display: flex; }' +
    '@media (max-width: 650px) { .inseam-container { display: block !important; } }' +
    '.inseam-table { width: 100%; border-collapse: collapse; font: 14px "Poppins", sans-serif; text-align: center; }' +
    '.inseam-table th, .inseam-table td { padding: 10px; border: 1px solid #ddd; }' +
    '.inseam-table thead { background: #f4f4f4; }' +
    '.inseam-table td:first-child { font-weight: 500; background: #fafafa; text-align: left; }' +
    '.inseam-section { max-width: 400px; margin: auto; margin-top: 30px; }' +
    '.measure-image img { max-width: 100%; height: auto; }' +
    '</style>' +
    '<script>' +
    'var tableIn=document.getElementById("sizeTableIn");' +
    'var tableCm=document.getElementById("sizeTableCm");' +
    'var sizeHeading=document.querySelector(".size-heading");' +
    'var toggleContainer=document.getElementById("unitToggle");' +
    'var switchEl=toggleContainer.querySelector(".switch");' +
    'var inLabel=toggleContainer.querySelector(".in-label");' +
    'var cmLabel=toggleContainer.querySelector(".cm-label");' +
    'var inseamContainer=document.querySelector(".inseam-container");' +
    'var inseam_table_in=document.querySelector(".inseam-table-in");' +
    'var inseam_table_cm=document.querySelector(".inseam-table-cm");' +
    'var pantin=document.querySelector(".pantin");' +
    'var pantcm=document.querySelector(".pantcm");' +
    'var jumpsuitin=document.querySelector(".jumpsuitin");' +
    'var jumpsuitcm=document.querySelector(".jumpsuitcm");' +
    'document.getElementById("closeModal").onclick=function(){parent.postMessage("dept-size-chart-close","*")};' +
    'switchEl.addEventListener("click",function(){' +
    'switchEl.classList.toggle("active");' +
    'if(switchEl.classList.contains("active")){' +
    'tableIn.style.display="none";tableCm.style.display="table";cmLabel.classList.add("active");inLabel.classList.remove("active");' +
    'inseam_table_in.style.display="none";inseam_table_cm.style.display="table";' +
    'pantin.style.display="none";pantcm.style.display="table";' +
    'jumpsuitin.style.display="none";jumpsuitcm.style.display="table";' +
    '}else{' +
    'tableIn.style.display="table";tableCm.style.display="none";inLabel.classList.add("active");cmLabel.classList.remove("active");' +
    'inseam_table_in.style.display="table";inseam_table_cm.style.display="none";' +
    'pantin.style.display="table";pantcm.style.display="none";' +
    'jumpsuitin.style.display="table";jumpsuitcm.style.display="none";' +
    '}});' +
    'function displaySizeChart(productType){' +
    'var currentProductType=productType;' +
    'var inchTable=document.getElementById("sizeTableIn");' +
    'var cmTable=document.getElementById("sizeTableCm");' +
    'var measure=document.querySelector(".how-to-measure");' +
    'cmTable.style.display="none";inchTable.style.display="table";' +
    'switchEl.classList.remove("active");inLabel.classList.add("active");cmLabel.classList.remove("active");' +
    'inseam_table_in.style.display="table";inseam_table_cm.style.display="none";' +
    'pantin.style.display="table";pantcm.style.display="none";' +
    'jumpsuitin.style.display="table";jumpsuitcm.style.display="none";' +
    'var inTableHtml="",cmTableHtml="",measureHtml="",sizeHeadingText="";' +
    'if(currentProductType&&currentProductType.toLowerCase()==="women tops"){' +
    'sizeHeadingText="Women\\\'s Top";' +
    'inTableHtml=\'<thead><tr><th>Size</th><th>XXS</th><th>XS</th><th>S</th><th>M</th><th>L</th><th>XL</th><th>2XL</th><th>3XL</th><th>4XL</th><th>5XL</th></tr></thead><tbody><tr><td>Bust</td><td>33.5</td><td>37.5</td><td>39.5</td><td>41.5</td><td>44</td><td>47</td><td>50.5</td><td>54.5</td><td>58.5</td><td>62.5</td></tr><tr><td>Waist</td><td>33</td><td>35</td><td>37</td><td>39</td><td>41.5</td><td>44.5</td><td>48</td><td>52</td><td>56</td><td>60</td></tr><tr><td>Sweep</td><td>37.5</td><td>39.5</td><td>41.5</td><td>43.5</td><td>46.5</td><td>50</td><td>54</td><td>58</td><td>62</td><td>66.5</td></tr></tbody>\';' +
    'cmTableHtml=\'<thead><tr><th>Size</th><th>XXS</th><th>XS</th><th>S</th><th>M</th><th>L</th><th>XL</th><th>2XL</th><th>3XL</th><th>4XL</th><th>5XL</th></tr></thead><tbody><tr><td>Bust</td><td>85.09</td><td>95.3</td><td>100.3</td><td>105.4</td><td>111.8</td><td>119.4</td><td>128.3</td><td>138.4</td><td>148.6</td><td>158.8</td></tr><tr><td>Waist</td><td>83.8</td><td>88.9</td><td>94.0</td><td>99.1</td><td>105.4</td><td>113.0</td><td>121.9</td><td>132.1</td><td>142.2</td><td>152.4</td></tr><tr><td>Sweep</td><td>95.3</td><td>100.3</td><td>105.4</td><td>110.5</td><td>118.1</td><td>127.0</td><td>137.2</td><td>147.3</td><td>157.5</td><td>168.9</td></tr></tbody>\';' +
    'measureHtml=\'<div class="measure-image"><img src="https://cdn.shopify.com/s/files/1/0928/8869/5155/files/WhatsApp_Image_2025-09-09_at_10.42.16_AM_3.png?v=1757914211" alt="How to Measure Illustration"></div><div class="measure-text"><h2>How To Measure</h2><ul><li><strong>BUST</strong> Measure around the fullest part of your bust</li><li><strong>WAIST</strong> Measure around the smallest part of your waist</li><li><strong>SWEEP</strong> Measure around the fullest part of your hip</li></ul></div>\';' +
    'inseamContainer.innerHTML="";inseamContainer.style.display="none";' +
    '}else if(currentProductType&&(currentProductType.toLowerCase()==="women pants"||currentProductType.toLowerCase()==="women joggers")){' +
    'sizeHeadingText="Women\\\'s Pants";' +
    'inTableHtml=\'<thead><tr><th>Size</th><th>XXS</th><th>XS</th><th>S</th><th>M</th><th>L</th><th>XL</th><th>XXL</th><th>3XL</th><th>4XL</th><th>5XL</th></tr></thead><tbody><tr><td>Waist</td><td>27</td><td>29</td><td>31</td><td>33</td><td>36</td><td>39</td><td>43</td><td>47</td><td>51</td><td>56</td></tr><tr><td>Hip</td><td>40</td><td>42</td><td>44</td><td>47</td><td>50</td><td>53</td><td>57</td><td>61</td><td>66</td><td>71</td></tr></tbody>\';' +
    'cmTableHtml=\'<thead><tr><th>Size</th><th>XXS</th><th>XS</th><th>S</th><th>M</th><th>L</th><th>XL</th><th>2XL</th><th>3XL</th><th>4XL</th><th>5XL</th></tr></thead><tbody><tr><td>Waist</td><td>68.6</td><td>73.7</td><td>78.7</td><td>83.8</td><td>91.4</td><td>99.1</td><td>109.2</td><td>119.4</td><td>129.5</td><td>142.2</td></tr><tr><td>Hip</td><td>101.6</td><td>106.7</td><td>111.8</td><td>119.4</td><td>127.0</td><td>134.6</td><td>144.8</td><td>154.9</td><td>167.6</td><td>180.3</td></tr></tbody>\';' +
    'measureHtml=\'<div class="measure-image"><img src="https://cdn.shopify.com/s/files/1/0928/8869/5155/files/Measurement_details_2_167e807b-9618-473f-8343-44cec0e91875.png?v=1757915735" alt="How to Measure Illustration"></div><div class="measure-text"><h2>How To Measure</h2><ul><li><strong>INSEAM</strong> Measure from the top of your inner thigh down to the bottom of your ankle along the inside of your leg.</li><li><strong>WAIST</strong> Measure around the smallest part of your waist</li><li><strong>HIP</strong> Measure around the fullest part of your hip</li></ul></div>\';' +
    'inseam_table_in.innerHTML=\'<thead><tr><th colspan="2">INSEAM LENGTH JOGGERS</th></tr></thead><tbody><tr><td>PETITE</td><td>26</td></tr><tr><td>REGULAR</td><td>29</td></tr><tr><td>TALL</td><td>31</td></tr></tbody>\';' +
    'inseam_table_cm.innerHTML=\'<thead><tr><th colspan="2">INSEAM LENGTH JOGGERS</th></tr></thead><tbody><tr><td>PETITE</td><td>66</td></tr><tr><td>REGULAR</td><td>74</td></tr><tr><td>TALL</td><td>79</td></tr></tbody>\';' +
    'pantin.innerHTML=\'<thead><tr><th>Inseam Length</th><th>Cargo</th><th>Slim</th></tr></thead><tbody><tr><td>Petite</td><td>29</td><td>27</td></tr><tr><td>Regular</td><td>31</td><td>30</td></tr><tr><td>Tall</td><td>34</td><td>33</td></tr></tbody>\';' +
    'pantcm.innerHTML=\'<thead><tr><th>Inseam Length</th><th>Cargo</th><th>Slim</th></tr></thead><tbody><tr><td>Petite</td><td>74</td><td>69</td></tr><tr><td>Regular</td><td>79</td><td>76</td></tr><tr><td>Tall</td><td>86</td><td>84</td></tr></tbody>\';' +
    'inseamContainer.style.display="flex";' +
    '}else if(currentProductType&&currentProductType.toLowerCase()==="women jackets"){' +
    'sizeHeadingText="Women\\\'s Jacket";' +
    'inTableHtml=\'<thead><tr><th>Size</th><th>XXS</th><th>XS</th><th>S</th><th>M</th><th>L</th><th>XL</th><th>2XL</th><th>3XL</th><th>4XL</th><th>5XL</th></tr></thead><tbody><tr><td>Bust</td><td>36</td><td>38</td><td>40</td><td>42</td><td>44</td><td>46.5</td><td>49.5</td><td>52.5</td><td>56.5</td><td>60.5</td></tr><tr><td>Waist</td><td>33.5</td><td>35.5</td><td>37.5</td><td>39.5</td><td>41.5</td><td>44</td><td>47</td><td>50</td><td>54</td><td>58</td></tr><tr><td>Sweep</td><td>38</td><td>40</td><td>42</td><td>44</td><td>46</td><td>49</td><td>52</td><td>55.5</td><td>59.5</td><td>64</td></tr></tbody>\';' +
    'cmTableHtml=\'<thead><tr><th>Size</th><th>XXS</th><th>XS</th><th>S</th><th>M</th><th>L</th><th>XL</th><th>2XL</th><th>3XL</th><th>4XL</th><th>5XL</th></tr></thead><tbody><tr><td>Bust</td><td>91.4</td><td>96.5</td><td>101.6</td><td>106.7</td><td>111.8</td><td>118.1</td><td>125.7</td><td>133.4</td><td>143.5</td><td>153.7</td></tr><tr><td>Waist</td><td>85.1</td><td>90.2</td><td>95.3</td><td>100.3</td><td>105.4</td><td>111.8</td><td>119.4</td><td>127.0</td><td>137.2</td><td>147.3</td></tr><tr><td>Sweep</td><td>96.5</td><td>101.6</td><td>106.7</td><td>111.8</td><td>116.8</td><td>124.5</td><td>132.1</td><td>141.0</td><td>151.1</td><td>162.6</td></tr></tbody>\';' +
    'measureHtml=\'<div class="measure-image"><img src="https://cdn.shopify.com/s/files/1/0928/8869/5155/files/WhatsApp_Image_2025-09-09_at_10.42.16_AM_3.png?v=1757914211" alt="How to Measure Illustration"></div><div class="measure-text"><h2>How To Measure</h2><ul><li><strong>BUST</strong> Measure around the fullest part of your bust</li><li><strong>WAIST</strong> Measure around the smallest part of your waist</li><li><strong>SWEEP</strong> Measure around the fullest part of your sweep</li></ul></div>\';' +
    'inseamContainer.innerHTML="";inseamContainer.style.display="none";' +
    '}else if(currentProductType&&currentProductType.toLowerCase()==="women jumpsuits"){' +
    'sizeHeadingText="Women\\\'s Jumpsuit";' +
    'inTableHtml=\'<thead><tr><th>Size</th><th>XXS</th><th>XS</th><th>S</th><th>M</th><th>L</th><th>XL</th><th>2XL</th><th>3XL</th><th>4XL</th><th>5XL</th></tr></thead><tbody><tr><td>Bust</td><td>37</td><td>39</td><td>41</td><td>43</td><td>45</td><td>47.5</td><td>50.5</td><td>53.5</td><td>57.5</td><td>61.5</td></tr><tr><td>Waist</td><td>28.5</td><td>30.5</td><td>32.5</td><td>34.5</td><td>37.5</td><td>40.5</td><td>44.5</td><td>48.5</td><td>52.5</td><td>57.5</td></tr><tr><td>Hip</td><td>40</td><td>42</td><td>44</td><td>47</td><td>50</td><td>53</td><td>57</td><td>61</td><td>66</td><td>71</td></tr></tbody>\';' +
    'cmTableHtml=\'<thead><tr><th>Size</th><th>XXS</th><th>XS</th><th>S</th><th>M</th><th>L</th><th>XL</th><th>2XL</th><th>3XL</th><th>4XL</th><th>5XL</th></tr></thead><tbody><tr><td>Bust</td><td>94.0</td><td>99.1</td><td>104.1</td><td>109.2</td><td>114.3</td><td>120.7</td><td>128.3</td><td>135.9</td><td>146.1</td><td>156.2</td></tr><tr><td>Waist</td><td>72.4</td><td>77.5</td><td>82.6</td><td>87.6</td><td>95.3</td><td>102.9</td><td>113.0</td><td>123.2</td><td>133.4</td><td>146.1</td></tr><tr><td>Hip</td><td>101.6</td><td>106.7</td><td>111.8</td><td>119.4</td><td>127.0</td><td>134.6</td><td>144.8</td><td>154.9</td><td>167.6</td><td>180.3</td></tr></tbody>\';' +
    'measureHtml=\'<div class="measure-image"><img src="https://cdn.shopify.com/s/files/1/0928/8869/5155/files/Group_9.png?v=1757916929" alt="How to Measure Illustration"></div><div class="measure-text"><h2>How To Measure</h2><ul><li><strong>BUST</strong> Measure around the fullest part of your bust</li><li><strong>WAIST</strong> Measure around the smallest part of your waist</li><li><strong>HIP</strong> Measure around the fullest part of your hip</li><li><strong>INSEAM</strong> Measure from the top of your inner thigh down to the bottom of your ankle along the inside of your leg.</li></ul></div>\';' +
    'jumpsuitin.innerHTML=\'<thead><tr><th colspan="2">INSEAM LENGTH JUMPSUIT</th></tr></thead><tbody><tr><td>Petite</td><td>28</td></tr><tr><td>Regular</td><td>30</td></tr><tr><td>Tall</td><td>33</td></tr></tbody>\';' +
    'jumpsuitcm.innerHTML=\'<thead><tr><th colspan="2">INSEAM LENGTH JUMPSUIT</th></tr></thead><tbody><tr><td>Petite</td><td>71</td></tr><tr><td>Regular</td><td>76</td></tr><tr><td>Tall</td><td>84</td></tr></tbody>\';' +
    'inseamContainer.style.display="flex";' +
    '}else if(currentProductType&&currentProductType.toLowerCase()==="men tops"){' +
    'sizeHeadingText="Men\\\'s Tops";' +
    'inTableHtml=\'<thead><tr><th>Size</th><th>XS</th><th>S</th><th>M</th><th>L</th><th>XL</th><th>2XL</th><th>3XL</th><th>4XL</th><th>5XL</th></tr></thead><tbody><tr><td>Chest</td><td>40.5</td><td>42.5</td><td>44.5</td><td>47</td><td>49.5</td><td>52.5</td><td>55.5</td><td>59.5</td><td>63.5</td></tr><tr><td>Sweep</td><td>39.5</td><td>41.5</td><td>43.5</td><td>46</td><td>48.5</td><td>51.5</td><td>54.5</td><td>58.5</td><td>62.5</td></tr></tbody>\';' +
    'cmTableHtml=\'<thead><tr><th>Size</th><th>XS</th><th>S</th><th>M</th><th>L</th><th>XL</th><th>2XL</th><th>3XL</th><th>4XL</th><th>5XL</th></tr></thead><tbody><tr><td>Chest</td><td>102.9</td><td>108.0</td><td>113.0</td><td>119.4</td><td>125.7</td><td>133.4</td><td>141.0</td><td>151.1</td><td>161.3</td></tr><tr><td>Sweep</td><td>100.3</td><td>105.4</td><td>110.5</td><td>116.8</td><td>123.2</td><td>130.8</td><td>138.4</td><td>148.6</td><td>158.8</td></tr></tbody>\';' +
    'measureHtml=\'<div class="measure-image"><img src="https://cdn.shopify.com/s/files/1/0928/8869/5155/files/Group_11.png?v=1757927862" alt="How to Measure Illustration"></div><div class="measure-text"><h2>How To Measure</h2><ul><li><strong>CHEST</strong> Measure around the fullest part of your bust</li><li><strong>WAIST</strong> Measure around the smallest part of your waist</li></ul></div>\';' +
    'inseamContainer.innerHTML="";inseamContainer.style.display="none";' +
    '}else if(currentProductType&&currentProductType.toLowerCase()==="men pants"){' +
    'sizeHeadingText="Men\\\'s Pant";' +
    'inTableHtml=\'<thead><tr><th>Size</th><th>XS</th><th>S</th><th>M</th><th>L</th><th>XL</th><th>2XL</th><th>3XL</th><th>4XL</th><th>5XL</th></tr></thead><tbody><tr><td>Waist</td><td>29</td><td>31</td><td>33</td><td>36</td><td>39</td><td>43</td><td>47</td><td>51</td><td>56</td></tr><tr><td>Hip</td><td>42</td><td>44</td><td>47</td><td>50</td><td>53</td><td>57</td><td>62</td><td>67</td><td>73</td></tr><tr><td>INSEAM LENGTH REGULAR PANT</td><td>31</td><td>31</td><td>31</td><td>31</td><td>31</td><td>31</td><td>31</td><td>31</td><td>31</td></tr><tr><td>INSEAM LENGTH REGULAR JOGGERS</td><td>29</td><td>29</td><td>29</td><td>29</td><td>29</td><td>29</td><td>29</td><td>29</td><td>29</td></tr></tbody>\';' +
    'cmTableHtml=\'<thead><tr><th>Size</th><th>XS</th><th>S</th><th>M</th><th>L</th><th>XL</th><th>2XL</th><th>3XL</th><th>4XL</th><th>5XL</th></tr></thead><tbody><tr><td>Waist</td><td>73.66</td><td>78.74</td><td>83.82</td><td>91.44</td><td>99.06</td><td>109.22</td><td>119.38</td><td>129.54</td><td>142.24</td></tr><tr><td>Hip</td><td>106.68</td><td>111.76</td><td>119.38</td><td>127.00</td><td>134.62</td><td>144.78</td><td>157.48</td><td>170.18</td><td>185.42</td></tr><tr><td>INSEAM LENGTH REGULAR</td><td>78.74</td><td>78.74</td><td>78.74</td><td>78.74</td><td>78.74</td><td>78.74</td><td>78.74</td><td>78.74</td><td>78.74</td></tr></tbody>\';' +
    'measureHtml=\'<div class="measure-image"><img src="https://cdn.shopify.com/s/files/1/0928/8869/5155/files/pant.png?v=1757918762" alt="How to Measure Illustration"></div><div class="measure-text"><h2>How To Measure</h2><ul><li><strong>INSEAM</strong> Measure from the top of your inner thigh down to the bottom of your ankle along the inside of your leg.</li><li><strong>WAIST</strong> Measure around the smallest part of your waist</li><li><strong>HIP</strong> Measure around the fullest part of your hip</li></ul></div>\';' +
    'inseamContainer.innerHTML="";inseamContainer.style.display="none";' +
    '}else if(currentProductType&&currentProductType.toLowerCase()==="men jackets"){' +
    'sizeHeadingText="Men\\\'s Jacket";' +
    'inTableHtml=\'<thead><tr><th>Size</th><th>XS</th><th>S</th><th>M</th><th>L</th><th>XL</th><th>2XL</th><th>3XL</th><th>4XL</th><th>5XL</th></tr></thead><tbody><tr><td>Chest</td><td>40</td><td>42</td><td>44</td><td>46.5</td><td>49</td><td>52</td><td>55</td><td>59</td><td>63</td></tr><tr><td>Waist</td><td>40</td><td>42</td><td>44</td><td>46.5</td><td>49</td><td>52</td><td>55</td><td>59</td><td>63</td></tr><tr><td>Sweep</td><td>40</td><td>42</td><td>44</td><td>46.5</td><td>49</td><td>52</td><td>55</td><td>59</td><td>63</td></tr></tbody>\';' +
    'cmTableHtml=\'<thead><tr><th>Size</th><th>XS</th><th>S</th><th>M</th><th>L</th><th>XL</th><th>2XL</th><th>3XL</th><th>4XL</th><th>5XL</th></tr></thead><tbody><tr><td>Chest</td><td>101.6</td><td>106.68</td><td>111.76</td><td>118.11</td><td>124.46</td><td>132.08</td><td>139.7</td><td>149.86</td><td>160.02</td></tr><tr><td>Waist</td><td>101.6</td><td>106.68</td><td>111.76</td><td>118.11</td><td>124.46</td><td>132.08</td><td>139.7</td><td>149.86</td><td>160.02</td></tr><tr><td>Sweep</td><td>101.6</td><td>106.68</td><td>111.76</td><td>118.11</td><td>124.46</td><td>132.08</td><td>139.7</td><td>149.86</td><td>160.02</td></tr></tbody>\';' +
    'measureHtml=\'<div class="measure-image"><img src="https://cdn.shopify.com/s/files/1/0928/8869/5155/files/JACKK.png?v=1758036203" alt="How to Measure Illustration"></div><div class="measure-text"><h2>How To Measure</h2><ul><li><strong>CHEST</strong> Measure around the fullest part of your bust</li><li><strong>WAIST</strong> Measure around the smallest part of your waist</li><li><strong>SWEEP</strong> Measure around the bottom hem of a garment</li></ul></div>\';' +
    'inseamContainer.innerHTML="";inseamContainer.style.display="none";' +
    '}' +
    'inchTable.innerHTML=inTableHtml;cmTable.innerHTML=cmTableHtml;measure.innerHTML=measureHtml;sizeHeading.innerHTML=sizeHeadingText;' +
    '}' +
    'displaySizeChart("__PRODUCT_TYPE__");' +
    '<\/script></body></html>';
    var ratingCache = {};
    var videoCache = {};
    var accordionContentCache = {};
    var htmlFetchCache = {};
    var productJsonCache = {};
    var productJsonFetchCache = {};
    var latestPdpRequestToken = 0;
    var activePdpVisibilityHandler = null;
    var syncPDPThumbRailOnResize = null;
    var colorOptionNames = parseColorOptionNames('');

    function normalizeToken(value) {
      if (value === null || value === undefined) return '';
      return String(value).toLowerCase().trim().replace(/[^a-z0-9]+/g, '');
    }

    function parseColorOptionNames(rawValue) {
      var base = String(rawValue || '').trim();
      if (!base) {
        return DEFAULT_COLOR_OPTION_NAMES.slice();
      }

      var parsed = base.split(',').map(function (name) {
        return normalizeToken(name);
      }).filter(Boolean);

      return parsed.length ? parsed : DEFAULT_COLOR_OPTION_NAMES.slice();
    }

    function isColorOptionName(optionName, configuredNames) {
      var normalizedOptionName = normalizeToken(optionName);
      if (!normalizedOptionName) return false;
      return configuredNames.indexOf(normalizedOptionName) !== -1;
    }

    function getSwatchHex(optionValue) {
      var key = normalizeToken(optionValue);
      return healixColorCodeCache[key] || '#cccccc';
    }

    function getProductColorValue(productLike) {
      if (!productLike || !productLike.options || !productLike.options.length) return '';

      var colorOptionIndex = -1;
      productLike.options.forEach(function (option, index) {
        var optionName = typeof option === 'string' ? option : option && option.name;
        if (colorOptionIndex === -1 && isColorOptionName(optionName, colorOptionNames)) {
          colorOptionIndex = index;
        }
      });

      if (colorOptionIndex === -1) return '';

      var firstVariant = deptFindFirstAvailableVariant(productLike.variants);
      if (firstVariant) {
        var optionKey = 'option' + (colorOptionIndex + 1);
        if (firstVariant[optionKey]) {
          return firstVariant[optionKey];
        }
      }

      var optionObj = productLike.options[colorOptionIndex];
      var optionValues = optionObj && optionObj.values ? optionObj.values : [];
      return optionValues.length ? optionValues[0] : '';
    }

    function getColorTokenFromHandle(handle) {
      if (!handle) return '';
      var parts = String(handle).split('-').filter(Boolean);
      if (!parts.length) return '';
      return normalizeToken(parts[parts.length - 1]);
    }

    function formatColorTokenLabel(token) {
      if (!token) return '';
      var withSpaces = token
        .replace(/([a-z])([0-9])/g, '$1 $2')
        .replace(/([0-9])([a-z])/g, '$1 $2');
      return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
    }

    function normalizeMoneyInput(rawValue) {
      if (rawValue === null || rawValue === undefined || rawValue === '') {
        return { major: 0, cents: 0, source: 'empty' };
      }

      if (typeof rawValue === 'string') {
        var sanitized = rawValue.replace(/,/g, '').trim();
        var majorFromString = Number(sanitized);
        if (!isFinite(majorFromString)) {
          return { major: 0, cents: 0, source: 'invalid-string' };
        }
        return {
          major: majorFromString,
          cents: Math.round(majorFromString * 100),
          source: 'string-major'
        };
      }

      var numeric = Number(rawValue);
      if (!isFinite(numeric)) {
        return { major: 0, cents: 0, source: 'invalid-number' };
      }

      if (Number.isInteger(numeric) && Math.abs(numeric) >= 1000) {
        return {
          major: numeric / 100,
          cents: Math.round(numeric),
          source: 'number-cents'
        };
      }

      return {
        major: numeric,
        cents: Math.round(numeric * 100),
        source: 'number-major'
      };
    }

    function formatMoney(rawValue) {
      var money = normalizeMoneyInput(rawValue);
      if (window.formatMoney) {
        return window.formatMoney(money.cents, window.themeMoneyFormat);
      }
      var hasShopifyFormatter = !!(window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.Shopify.money_format);
      if (hasShopifyFormatter) {
        return window.Shopify.formatMoney(money.cents, window.Shopify.money_format);
      }

      if (window.formatMoneyFallback) {
        return window.formatMoneyFallback(money.cents);
      }
      return money.major.toFixed(2);
    }

    function resizeImage(src, size) {
      if (!src) return '';
      if (typeof src !== 'string') src = src.src || '';
      if (!src) return '';
      var parts = src.split('?');
      var base = parts[0];
      var query = parts[1] ? '?' + parts[1] : '';
      var resized = base.replace(/(\.(jpg|jpeg|png|gif|webp))$/i, '_' + size + 'x$1');
      if (resized === base) return src;
      return resized + query;
    }

    function escapeHTML(str) {
      if (!str) return '';
      var div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    function deptIsVariantAvailable(variant) {
      if (!variant) return false;
      if (typeof variant.available === 'undefined') return true;
      return !!variant.available;
    }

    function deptFindFirstAvailableVariant(variants) {
      if (!Array.isArray(variants) || !variants.length) return null;
      return variants.find(function (variant) {
        return deptIsVariantAvailable(variant);
      }) || null;
    }

    function deptFindMatchingVariant(variants, selectedByIndex, requireAvailable) {
      if (!Array.isArray(variants) || !variants.length) return null;

      return variants.find(function (variant) {
        if (!variant) return false;
        if (requireAvailable && !deptIsVariantAvailable(variant)) return false;

        var option1Ok = selectedByIndex[0] === undefined || variant.option1 === selectedByIndex[0];
        var option2Ok = selectedByIndex[1] === undefined || variant.option2 === selectedByIndex[1];
        var option3Ok = selectedByIndex[2] === undefined || variant.option3 === selectedByIndex[2];
        return option1Ok && option2Ok && option3Ok;
      }) || null;
    }

    function pickBestVideoSources(sources) {
      if (!sources || !sources.length) return [];
      var dominated = sources.filter(function (s) {
        var t = (s.type || '').toLowerCase();
        return t.indexOf('video/') === 0;
      });
      if (!dominated.length) dominated = sources;
      dominated.sort(function (a, b) { return (a.width || 0) - (b.width || 0); });
      var best = null;
      for (var i = 0; i < dominated.length; i++) {
        if ((dominated[i].width || 0) >= 480) { best = dominated[i]; break; }
      }
      if (!best) best = dominated[dominated.length - 1];
      return [best];
    }

    function ensurePDPVideoAutoplay(videoEl) {
      if (!videoEl) return;
      videoEl.muted = true;
      videoEl.defaultMuted = true;
      videoEl.loop = true;
      videoEl.autoplay = true;
      videoEl.playsInline = true;
      videoEl.preload = 'auto';
      videoEl.controls = false;
      videoEl.removeAttribute('controls');
      videoEl.setAttribute('muted', '');
      videoEl.setAttribute('loop', '');
      videoEl.setAttribute('autoplay', '');
      videoEl.setAttribute('playsinline', '');

      var playVideo = function () {
        if (!videoEl || !videoEl.isConnected) return;
        var playPromise = videoEl.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(function () {});
        }
      };

      playVideo();

      if (videoEl.dataset.deptAutoplayBound !== 'true') {
        videoEl.dataset.deptAutoplayBound = 'true';
        videoEl.addEventListener('loadedmetadata', playVideo, { passive: true });
        videoEl.addEventListener('canplay', playVideo, { passive: true });
      }
    }

    function clearPDP() {
      if (activePdpVisibilityHandler) {
        document.removeEventListener('visibilitychange', activePdpVisibilityHandler);
        activePdpVisibilityHandler = null;
      }
      if (pdpArea) pdpArea.innerHTML = '';
    }

    function loadingPDPHTML() {
      return '<div class="dept__pdp-mount dept__pdp-mount--loading" data-pdp-mount>' +
             '<div class="dept__loading-skeleton pdp dept__pdp-skeleton-layer">' +
             '<div class="dept__skeleton-gallery">' +
               '<div class="dept__skeleton-thumbs">' +
                 '<div class="dept__skeleton-thumb"></div>' +
                 '<div class="dept__skeleton-thumb"></div>' +
                 '<div class="dept__skeleton-thumb"></div>' +
                 '<div class="dept__skeleton-thumb"></div>' +
               '</div>' +
               '<div class="dept__skeleton-main-img"></div>' +
             '</div>' +
             '<div class="dept__skeleton-info">' +
               '<div class="dept__skeleton-title"></div>' +
               '<div class="dept__skeleton-rating"></div>' +
               '<div class="dept__skeleton-price"></div>' +
               '<div class="dept__skeleton-swatches"></div>' +
               '<div class="dept__skeleton-options"></div>' +
               '<div class="dept__skeleton-options dept__skeleton-options--short"></div>' +
               '<div class="dept__skeleton-size-guide"></div>' +
               '<div class="dept__skeleton-btn"></div>' +
               '<div class="dept__skeleton-badges"></div>' +
               '<div class="dept__skeleton-accordion"></div>' +
               '<div class="dept__skeleton-accordion"></div>' +
               '<div class="dept__skeleton-accordion"></div>' +
               '<div class="dept__skeleton-accordion"></div>' +
             '</div>' +
             '</div></div>';
    }

    function fetchProductHTML(handle) {
      if (htmlFetchCache[handle]) return htmlFetchCache[handle];
      htmlFetchCache[handle] = fetch('/products/' + encodeURIComponent(handle), { headers: { 'Accept': 'text/html' } })
        .then(function (r) { return r.text(); })
        .catch(function () { return ''; });
      return htmlFetchCache[handle];
    }

    function parseVideosFromHTML(html) {
      if (!html) return [];
      var videos = [];
      try {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var videoEls = doc.querySelectorAll('video');
        for (var i = 0; i < videoEls.length; i++) {
          var vid = videoEls[i];
          var sources = [];
          var sourceEls = vid.querySelectorAll('source');
          for (var j = 0; j < sourceEls.length; j++) {
            var src = sourceEls[j].getAttribute('src') || '';
            var type = sourceEls[j].getAttribute('type') || '';
            if (src) sources.push({ url: src, type: type });
          }
          if (!sources.length) continue;
          var poster = vid.getAttribute('poster') || '';
          if (!poster) {
            var previewImg = vid.closest('[data-media-id]');
            if (previewImg) {
              var img = previewImg.querySelector('img');
              if (img) poster = img.getAttribute('src') || '';
            }
          }
          videos.push({ poster: poster, sources: sources });
        }
      } catch (e) {}
      return videos;
    }

    function fetchProductVideos(handle, callback) {
      if (videoCache[handle]) {
        callback(videoCache[handle]);
        return;
      }
      fetchProductHTML(handle).then(function (html) {
        var videos = parseVideosFromHTML(html);
        videoCache[handle] = videos;
        callback(videos);
      }).catch(function () {
        videoCache[handle] = [];
        callback([]);
      });
    }

    function fetchProductVideosPromise(handle) {
      return new Promise(function (resolve) {
        fetchProductVideos(handle, function (videos) {
          resolve(Array.isArray(videos) ? videos : []);
        });
      });
    }

    function injectVideoIntoPDP(normalizedHandle, requestToken) {
      fetchProductVideosPromise(normalizedHandle).then(function (videos) {
        if (requestToken !== latestPdpRequestToken || !pdpArea) return;
        if (!Array.isArray(videos) || !videos.length) return;
        var mainImage = pdpArea.querySelector('.dept__pdp-main-image');
        var thumbRail = pdpArea.querySelector('.dept__pdp-thumb-rail');
        if (!mainImage) return;

        var bestSources = pickBestVideoSources(videos[0].sources || []);
        var poster = videos[0].poster || '';
        var sourceTags = bestSources.map(function (s) {
          return '<source src="' + s.url + '" type="' + (s.type || 'video/mp4') + '">';
        }).join('');
        var videoHTML = '<video class="dept__pdp-media--video" autoplay loop muted playsinline preload="auto"' +
          (poster ? ' poster="' + poster + '"' : '') + '>' + sourceTags + '</video>';
        var videoItem = document.createElement('div');
        videoItem.className = 'dept__pdp-media-item';
        videoItem.setAttribute('data-media-idx', '0');
        videoItem.innerHTML = videoHTML;

        var existingItems = mainImage.querySelectorAll('.dept__pdp-media-item');
        for (var i = 0; i < existingItems.length; i++) {
          var oldIdx = parseInt(existingItems[i].getAttribute('data-media-idx'), 10);
          existingItems[i].setAttribute('data-media-idx', String(oldIdx + 1));
        }
        mainImage.insertBefore(videoItem, mainImage.firstChild);

        var activeItem = mainImage.querySelector('.dept__pdp-media-item.is-active');
        if (activeItem) activeItem.classList.remove('is-active');
        videoItem.classList.add('is-active');

        if (thumbRail) {
          var thumbHTML = '<div class="dept__pdp-thumb dept__pdp-thumb--active dept__pdp-thumb--video" data-media-index="0">' +
            (poster ? '<img src="' + poster + '" alt="" loading="lazy">' : '') + '</div>';
          var existingThumbs = thumbRail.querySelectorAll('.dept__pdp-thumb');
          for (var j = 0; j < existingThumbs.length; j++) {
            existingThumbs[j].classList.remove('dept__pdp-thumb--active');
            var oldThumbIdx = parseInt(existingThumbs[j].getAttribute('data-media-index'), 10);
            existingThumbs[j].setAttribute('data-media-index', String(oldThumbIdx + 1));
          }
          thumbRail.insertAdjacentHTML('afterbegin', thumbHTML);
        }

        var vid = videoItem.querySelector('video');
        if (vid) ensurePDPVideoAutoplay(vid);
      });
    }

    function parseRatingFromProductHTML(html) {
      var data = { rating: 0, count: 0 };
      if (!html) return data;

      var match = html.match(/data-average-rating=['"]([0-9.]+)['"][\s\S]*?data-number-of-reviews=['"](\d+)['"]/);
      if (match) {
        data.rating = parseFloat(match[1]) || 0;
        data.count = parseInt(match[2], 10) || 0;
      }
      return data;
    }

    function buildStarsHTML(ratingValue, productId) {
      var starColor = '#facc15';
      var emptyColor = '#d1d5db';
      var starSize = 14;
      var html = '';
      for (var i = 1; i <= 5; i++) {
        var fill = 0;
        if (ratingValue >= i) fill = 100;
        else if (ratingValue > i - 1) fill = Math.round((ratingValue - (i - 1)) * 100);

        if (fill === 100) {
          html += '<svg xmlns="http://www.w3.org/2000/svg" width="' + starSize + '" height="' + starSize + '" viewBox="0 0 24 24" fill="' + starColor + '" stroke="' + starColor + '" stroke-width="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>';
        } else if (fill === 0) {
          html += '<svg xmlns="http://www.w3.org/2000/svg" width="' + starSize + '" height="' + starSize + '" viewBox="0 0 24 24" fill="' + emptyColor + '" stroke="' + emptyColor + '" stroke-width="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>';
        } else {
          var gid = 'dept-star-' + (productId || 0) + '-' + i;
          html += '<svg xmlns="http://www.w3.org/2000/svg" width="' + starSize + '" height="' + starSize + '" viewBox="0 0 24 24" stroke-width="1.5"><defs><linearGradient id="' + gid + '"><stop offset="' + fill + '%" stop-color="' + starColor + '"/><stop offset="' + fill + '%" stop-color="' + emptyColor + '"/></linearGradient></defs><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" fill="url(#' + gid + ')" stroke="url(#' + gid + ')"/></svg>';
        }
      }
      return html;
    }

    function buildSoldBadgeHTML(product) {
      var pid = product.id || 0;
      var d = new Date();
      var dateNum = parseInt('' + d.getUTCFullYear() + ('0' + (d.getUTCMonth() + 1)).slice(-2) + ('0' + d.getUTCDate()).slice(-2), 10);
      var pidLow = pid % 1000000;
      var seedA = (pidLow * 31417) % 1000003;
      var seedB = ((dateNum % 1000000) * 7919) % 1000003;
      var combined = (seedA + seedB) % 1000003;
      var minC = 50, maxC = 300;
      var count = (combined % (maxC - minC + 1)) + minC;
      return '<div class="dept__pdp-sold-badge">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>' +
        '<span class="dept__pdp-sold-badge-text">' + count + '+ sold</span>' +
        '<span class="dept__pdp-sold-badge-sep">&middot;</span>' +
        '<span class="dept__pdp-sold-badge-time">in the last 24 hours</span>' +
      '</div>';
    }

    function buildRatingHTML() {
      return '<div class="dept__pdp-rating" data-pdp-rating></div>';
    }

    function renderRatingEl(el, data, productId) {
      if (!data.count && !data.rating) {
        el.style.display = 'none';
        return;
      }
      var reviewsText = data.count === 1 ? 'review' : 'reviews';
      el.innerHTML =
        '<span class="dept__pdp-rating-stars">' + buildStarsHTML(data.rating, productId) + '</span>' +
        '<span class="dept__pdp-rating-text">' + data.rating.toFixed(1) + ' (' + data.count + ' ' + reviewsText + ')</span>';
      el.style.display = '';
    }

    function hydrateRating(product, callback) {
      var done = function () { if (callback) { callback(); callback = null; } };
      if (!pdpArea || !product || !product.handle) { done(); return; }
      var ratingEl = pdpArea.querySelector('[data-pdp-rating]');
      if (!ratingEl) { done(); return; }

      var handle = product.handle;
      if (ratingCache[handle]) {
        renderRatingEl(ratingEl, ratingCache[handle], product.id);
        done();
        return;
      }

      fetchProductHTML(handle)
        .then(function (html) {
          var data = parseRatingFromProductHTML(html);
          ratingCache[handle] = data;
          if (!videoCache[handle]) {
            videoCache[handle] = parseVideosFromHTML(html);
          }
          var activeHandle = String(pdpArea.dataset.currentProductHandle || '').trim();
          if (activeHandle === handle) {
            renderRatingEl(ratingEl, data, product.id);
          }
          done();
        })
        .catch(function () {
          ratingEl.style.display = 'none';
          done();
        });
    }

    function updatePDPOptionAvailability(variants, selectedByIndex) {
      if (!pdpArea || !variants || !variants.length) return;

      var optionRows = Array.prototype.slice.call(
        pdpArea.querySelectorAll('.dept__pdp-variants[data-option-index]')
      );

      optionRows.forEach(function (row) {
        var optionIndex = parseInt(row.dataset.optionIndex, 10);
        if (isNaN(optionIndex)) return;

        var rowButtons = Array.prototype.slice.call(
          row.querySelectorAll('.dept__pdp-option-btn')
        );

        rowButtons.forEach(function (button) {
          var optionValue = button.dataset.optionValue || '';
          var nextSelection = {
            0: selectedByIndex[0],
            1: selectedByIndex[1],
            2: selectedByIndex[2]
          };
          nextSelection[optionIndex] = optionValue;

          var hasMatchingVariant = false;
          var hasAvailableVariant = false;

          variants.forEach(function (variant) {
            var option1Ok = nextSelection[0] === undefined || variant.option1 === nextSelection[0];
            var option2Ok = nextSelection[1] === undefined || variant.option2 === nextSelection[1];
            var option3Ok = nextSelection[2] === undefined || variant.option3 === nextSelection[2];

            if (option1Ok && option2Ok && option3Ok) {
              hasMatchingVariant = true;
              if (deptIsVariantAvailable(variant)) {
                hasAvailableVariant = true;
              }
            }
          });

          var shouldDisable = !hasMatchingVariant || !hasAvailableVariant;
          button.disabled = shouldDisable;
          button.classList.toggle('is-unavailable', shouldDisable);
        });
      });
    }

    function syncVariantState(variants, selectedByIndex) {
      if (!variants.length) return;

      var matched = deptFindMatchingVariant(variants, selectedByIndex, true)
        || deptFindMatchingVariant(variants, selectedByIndex, false)
        || null;
      var fallbackVariant = deptFindFirstAvailableVariant(variants);
      var displayVariant = matched || fallbackVariant;

      var priceNode = pdpArea.querySelector('[data-pdp-price]');
      var compareNode = pdpArea.querySelector('[data-pdp-compare]');
      var atcButton = pdpArea.querySelector('.dept__pdp-atc');

      if (priceNode) priceNode.textContent = formatMoney(displayVariant ? displayVariant.price : 0);
      if (compareNode) {
        if (displayVariant && displayVariant.compare_at_price &&
            normalizeMoneyInput(displayVariant.compare_at_price).cents > normalizeMoneyInput(displayVariant.price).cents) {
          compareNode.innerHTML = '<span class="dept__pdp-compare">' + formatMoney(displayVariant.compare_at_price) + '</span>';
        } else {
          compareNode.innerHTML = '';
        }
      }

      if (atcButton) {
        var variantSellable = deptIsVariantAvailable(matched);
        atcButton.dataset.variantId = matched && matched.id ? matched.id : '';
        atcButton.disabled = !variantSellable;
        atcButton.textContent = variantSellable ? 'Add to Cart' : 'Sold Out';
      }

      var colorTag = pdpArea.querySelector('[data-dept-pdp-color-tag]');
      if (colorTag) {
        var currentHandle = pdpArea.dataset.currentProductHandle || '';
        var newColor = '';
        if (displayVariant) {
          newColor = getProductColorValue({ options: [], variants: [displayVariant] });
        }
        if (!newColor && currentHandle) {
          newColor = formatColorTokenLabel(getColorTokenFromHandle(currentHandle));
        }
        if (newColor) {
          colorTag.textContent = newColor;
          colorTag.style.display = '';
        }
      }
    }

    function bindPDPInteractions(product, mediaList, variants, selectedByIndex) {
      var thumbs = Array.prototype.slice.call(pdpArea.querySelectorAll('.dept__pdp-thumb'));
      var mainImage = pdpArea.querySelector('[data-main-image]');
      var thumbsRail = pdpArea.querySelector('.dept__pdp-thumbs');

      if (syncPDPThumbRailOnResize) {
        window.removeEventListener('resize', syncPDPThumbRailOnResize);
        syncPDPThumbRailOnResize = null;
      }

      function syncPDPThumbRailHeight() {
        if (!thumbsRail || !mainImage) return;

        var mainImageRect = mainImage.getBoundingClientRect();
        if (!mainImageRect || !mainImageRect.height) return;

        thumbsRail.style.setProperty('--dept-pdp-thumbs-max-height', Math.round(mainImageRect.height) + 'px');
      }

      function scrollThumbIntoView(thumb) {
        if (!thumb) return;

        try {
          thumb.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
            behavior: 'smooth'
          });
        } catch (error) {
          thumb.scrollIntoView();
        }
      }

      function switchMedia(newIndex) {
        if (!mainImage) return;
        var currentItem = mainImage.querySelector('.dept__pdp-media-item.is-active');
        var nextItem = mainImage.querySelector('[data-media-idx="' + newIndex + '"]');
        if (!nextItem || nextItem === currentItem) return;

        var currentHeight = mainImage.offsetHeight;
        mainImage.style.height = currentHeight + 'px';

        if (currentItem) {
          var oldVid = currentItem.querySelector('video');
          if (oldVid) oldVid.pause();
          currentItem.classList.remove('is-active');
        }

        nextItem.classList.add('is-active');
        var newVid = nextItem.querySelector('video');
        if (newVid) ensurePDPVideoAutoplay(newVid);

        window.requestAnimationFrame(function () {
          var newHeight = nextItem.offsetHeight || mainImage.scrollHeight;
          mainImage.style.height = newHeight + 'px';
        });

        syncPDPThumbRailHeight();
      }

      function onMainImageTransitionEnd(e) {
        if (e.propertyName === 'height' && e.target === mainImage) {
          mainImage.style.height = '';
          syncPDPThumbRailHeight();
        }
      }
      mainImage.addEventListener('transitionend', onMainImageTransitionEnd);

      var heightClearFallback = null;
      function scheduleHeightClear() {
        if (heightClearFallback) clearTimeout(heightClearFallback);
        heightClearFallback = setTimeout(function () {
          if (mainImage && mainImage.style.height) {
            mainImage.style.height = '';
            syncPDPThumbRailHeight();
          }
        }, 500);
      }

      syncPDPThumbRailHeight();
      syncPDPThumbRailOnResize = function () {
        mainImage.style.height = '';
        syncPDPThumbRailHeight();
      };
      window.addEventListener('resize', syncPDPThumbRailOnResize);

      var activeItem = mainImage ? mainImage.querySelector('.dept__pdp-media-item.is-active') : null;
      if (activeItem) {
        var initImg = activeItem.querySelector('img');
        var initVid = activeItem.querySelector('video');
        if (initImg && !initImg.complete) {
          initImg.addEventListener('load', syncPDPThumbRailHeight, { once: true });
        } else if (initVid) {
          ensurePDPVideoAutoplay(initVid);
          initVid.addEventListener('loadeddata', syncPDPThumbRailHeight, { once: true });
        }
      }

      if (activePdpVisibilityHandler) {
        document.removeEventListener('visibilitychange', activePdpVisibilityHandler);
      }
      activePdpVisibilityHandler = function () {
        if (document.visibilityState !== 'visible') return;
        var activeVideo = mainImage ? mainImage.querySelector('.dept__pdp-media-item.is-active video') : null;
        ensurePDPVideoAutoplay(activeVideo);
      };
      document.addEventListener('visibilitychange', activePdpVisibilityHandler);

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var index = parseInt(thumb.dataset.mediaIndex, 10);
          thumbs.forEach(function (item) { item.classList.remove('dept__pdp-thumb--active'); });
          thumb.classList.add('dept__pdp-thumb--active');
          scrollThumbIntoView(thumb);
          switchMedia(index);
          scheduleHeightClear();
        });
      });

      var optionButtons = Array.prototype.slice.call(
        pdpArea.querySelectorAll('.dept__pdp-option-btn:not(.dept__pdp-color-switch-btn)')
      );
      optionButtons.forEach(function (button) {
        button.addEventListener('click', function () {
          if (button.disabled) return;

          var optionIndex = parseInt(button.dataset.optionIndex, 10);
          var optionValue = button.dataset.optionValue || '';

          selectedByIndex[optionIndex] = optionValue;

          var row = button.closest('.dept__pdp-variants');
          if (row) {
            Array.prototype.slice.call(row.querySelectorAll('.dept__pdp-option-btn')).forEach(function (rowButton) {
              var isActive = rowButton === button;
              rowButton.classList.toggle('dept__pdp-option-btn--active', isActive);
              if (rowButton.classList.contains('dept__pdp-variant-btn')) {
                rowButton.classList.toggle('dept__pdp-variant-btn--active', isActive);
              }
              if (rowButton.classList.contains('dept__pdp-swatch-btn')) {
                rowButton.classList.toggle('dept__pdp-swatch-btn--active', isActive);
              }
            });
          }

          updatePDPOptionAvailability(variants, selectedByIndex);
          syncVariantState(variants, selectedByIndex);
        });
      });

      updatePDPOptionAvailability(variants, selectedByIndex);
      syncVariantState(variants, selectedByIndex);

      var atcButton = pdpArea.querySelector('.dept__pdp-atc');
      if (atcButton) {
        atcButton.addEventListener('click', function () {
          var variantId = parseInt(atcButton.dataset.variantId, 10);
          if (!variantId) return;

          atcButton.disabled = true;
          atcButton.textContent = 'Adding...';

          (window.CartAPI ? window.CartAPI.addItem(variantId, 1) : fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          }).then(function (r) { return r.json(); }))
            .then(function () {
              atcButton.textContent = 'Added!';
              setTimeout(function () {
                syncVariantState(variants, selectedByIndex);
              }, 1400);
            })
            .catch(function () {
              syncVariantState(variants, selectedByIndex);
            });
        });
      }

      var sizeChartBtn = pdpArea.querySelector('[data-dept-size-chart-open]');
      if (sizeChartBtn) {
        function buildSizeChartIframeHTML() {
          var productType = product.product_type || product.type || '';
          if (!productType) return null;
          return DEPT_SIZE_CHART_FULL_HTML.replace('__PRODUCT_TYPE__', productType.replace(/'/g, "\\'"));
        }

        var sizeChartIframeOverlay = null;

        function openSizeChartIframe() {
          var html = buildSizeChartIframeHTML();
          if (!html) return;

          sizeChartIframeOverlay = document.createElement('div');
          sizeChartIframeOverlay.style.cssText = 'position:fixed;inset:0;z-index:2147483001;background:rgba(0,0,0,0.5);display:flex;align-items:flex-start;justify-content:center;padding-top:4vh;';

          var modal = document.createElement('div');
          modal.style.cssText = 'background:#fff;width:96vw;max-width:1100px;height:92vh;max-height:92vh;border-radius:12px;overflow:hidden;box-shadow:0 25px 50px -12px rgba(0,0,0,0.25);';

          var iframe = document.createElement('iframe');
          iframe.style.cssText = 'width:100%;height:100%;border:none;display:block;';
          iframe.setAttribute('srcdoc', html);

          modal.appendChild(iframe);
          sizeChartIframeOverlay.appendChild(modal);
          document.body.appendChild(sizeChartIframeOverlay);
          document.body.style.overflow = 'hidden';

          sizeChartIframeOverlay.addEventListener('click', function (e) {
            if (e.target === sizeChartIframeOverlay) closeSizeChartIframe();
          });
        }

        function closeSizeChartIframe() {
          if (sizeChartIframeOverlay && sizeChartIframeOverlay.parentNode) {
            sizeChartIframeOverlay.parentNode.removeChild(sizeChartIframeOverlay);
          }
          sizeChartIframeOverlay = null;
          document.body.style.overflow = '';
        }

        window.addEventListener('message', function (e) {
          if (e.data === 'dept-size-chart-close') closeSizeChartIframe();
        });

        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && sizeChartIframeOverlay) closeSizeChartIframe();
        });

        sizeChartBtn.addEventListener('click', openSizeChartIframe);
      }
    }

    function renderPDP(product, collectionProductContext, videos) {
      if (!pdpArea || !product) {
        return;
      }

      var images = product.images || [];
      var variants = product.variants || [];
      videos = Array.isArray(videos) ? videos : [];

      if (collectionProductContext && Array.isArray(collectionProductContext.variants)) {
        var availMap = {};
        collectionProductContext.variants.forEach(function(cv) {
          if (cv.id) availMap[cv.id] = cv.available;
        });
        variants.forEach(function(v) {
          if (typeof v.available === 'undefined' && availMap.hasOwnProperty(v.id)) {
            v.available = availMap[v.id];
          }
        });
      }

      var firstVariant = deptFindFirstAvailableVariant(variants);
      var productAvailable = product.available === true || (firstVariant !== null);

      var mediaList = [];
      if (videos.length > 0) {
        var rawSources = videos[0].sources || [];
        var bestSources = pickBestVideoSources(rawSources);
        mediaList.push({ type: 'video', poster: videos[0].poster, sources: bestSources });
      }
      images.forEach(function (image) {
        mediaList.push({ type: 'image', src: image });
      });

      var thumbsHTML = mediaList.map(function (media, index) {
        var activeClass = index === 0 ? ' dept__pdp-thumb--active' : '';
        var videoClass = media.type === 'video' ? ' dept__pdp-thumb--video' : '';
        var thumbSrc = media.type === 'video'
          ? (media.poster || '')
          : resizeImage(media.src, 120);
        return (
          '<div class="dept__pdp-thumb' + activeClass + videoClass + '" data-media-index="' + index + '">' +
            (thumbSrc ? '<img src="' + thumbSrc + '" alt="" loading="lazy">' : '') +
          '</div>'
        );
      }).join('');

      var allMediaHTML = mediaList.map(function (media, idx) {
        var activeClass = idx === 0 ? ' is-active' : '';
        var inner = '';
        if (media.type === 'video') {
          var sourceTags = media.sources.map(function (s) {
            return '<source src="' + s.url + '" type="' + (s.type || 'video/mp4') + '">';
          }).join('');
          inner = '<video class="dept__pdp-media--video" ' + (idx === 0 ? 'autoplay ' : '') + 'loop muted playsinline preload="auto"' +
            (media.poster ? ' poster="' + media.poster + '"' : '') +
            '>' + sourceTags + '</video>';
        } else {
          inner = '<img class="dept__pdp-media--image" src="' + resizeImage(media.src, 900) + '" alt="' + escapeHTML(product.title) + '" loading="eager">';
        }
        return '<div class="dept__pdp-media-item' + activeClass + '" data-media-idx="' + idx + '">' + inner + '</div>';
      }).join('');

      var pdpColorVal = getProductColorValue(product);
      if (!pdpColorVal && product.handle) {
        var pdpColorToken = getColorTokenFromHandle(product.handle);
        pdpColorVal = formatColorTokenLabel(pdpColorToken);
      }
      var pdpColorTagHTML = pdpColorVal
        ? '<div class="dept__pdp-color-badge" data-dept-pdp-color-tag>' + escapeHTML(pdpColorVal) + '</div>'
        : '';

      var price = formatMoney(firstVariant ? firstVariant.price : 0);
      var comparePriceHTML = '';
      if (firstVariant && firstVariant.compare_at_price &&
          normalizeMoneyInput(firstVariant.compare_at_price).cents > normalizeMoneyInput(firstVariant.price).cents) {
        comparePriceHTML = '<span class="dept__pdp-compare">' + formatMoney(firstVariant.compare_at_price) + '</span>';
      }

      var options = product.options || [];
      var selectedByIndex = {};
      var colorSwitcherHTML = ''; // colour selector intentionally omitted (colour already chosen)
      var optionsHTML = '';

      if (firstVariant && firstVariant.option1 !== undefined) selectedByIndex[0] = firstVariant.option1;
      if (firstVariant && firstVariant.option2 !== undefined) selectedByIndex[1] = firstVariant.option2;
      if (firstVariant && firstVariant.option3 !== undefined) selectedByIndex[2] = firstVariant.option3;

      options.forEach(function (option, optionIndex) {
        if (
          option.name &&
          option.name.toLowerCase() === 'title' &&
          option.values &&
          option.values.length === 1 &&
          option.values[0] === 'Default Title'
        ) {
          return;
        }

        if (selectedByIndex[optionIndex] === undefined && option.values && option.values.length) {
          selectedByIndex[optionIndex] = option.values[0];
        }

        var optionName = option.name || 'Option';
        var isColorOption = isColorOptionName(optionName, colorOptionNames);
        var isLengthOption = optionName.toLowerCase().trim() === 'length';
        var rowClasses = 'dept__pdp-variants';
        if (isColorOption) {
          rowClasses += ' dept__pdp-variants--swatches';
        } else if (isLengthOption) {
          rowClasses += ' dept__pdp-variants--length';
        }

        optionsHTML +=
          '<div class="dept__pdp-option-section">' +
            '<span class="dept__pdp-option-label">' + escapeHTML(optionName) + '</span>' +
            '<div class="' + rowClasses + '" data-option-index="' + optionIndex + '" data-option-name="' + escapeHTML(optionName) + '">';

        (option.values || []).forEach(function (value, valueIndex) {
          var isInitiallyActive = selectedByIndex[optionIndex] === value;
          var escapedValue = escapeHTML(value);
          var activeClasses = isInitiallyActive ? ' dept__pdp-option-btn--active' : '';

          if (isColorOption) {
            var swatchHex = getSwatchHex(value);
            optionsHTML +=
              '<button type="button" class="dept__pdp-option-btn dept__pdp-swatch-btn' + activeClasses + (isInitiallyActive ? ' dept__pdp-swatch-btn--active' : '') + '"' +
                ' data-option-index="' + optionIndex + '"' +
                ' data-option-value="' + escapedValue + '"' +
                ' title="' + escapedValue + '"' +
                ' aria-label="' + escapeHTML(optionName + ': ' + value) + '">' +
                '<span class="dept__pdp-swatch-dot" style="--dept-pdp-swatch-color:' + swatchHex + ';"></span>' +
                '<span class="dept__pdp-swatch-label">' + escapedValue + '</span>' +
              '</button>';
          } else if (isLengthOption) {
            var lengthSubtext = '';
            var vLow = value.toLowerCase().trim();
            if (vLow === 'petite') lengthSubtext = '29\u2033 \u2013 Up to 5\u20195\u2033';
            else if (vLow === 'regular') lengthSubtext = '31\u2033 \u2013 5\u20196\u2033 to 5\u20198\u2033';
            else if (vLow === 'tall') lengthSubtext = '33\u2033 \u2013 5\u20199\u2033 and above';
            optionsHTML +=
              '<button type="button" class="dept__pdp-option-btn dept__pdp-variant-btn dept__pdp-variant-btn--length' + activeClasses + (isInitiallyActive ? ' dept__pdp-variant-btn--active' : '') + '"' +
                ' data-option-index="' + optionIndex + '"' +
                ' data-option-value="' + escapedValue + '">' +
                '<span class="dept__pdp-variant-main">' + escapedValue + '</span>' +
                (lengthSubtext ? '<small class="dept__pdp-variant-sub">' + lengthSubtext + '</small>' : '') +
              '</button>';
          } else {
            optionsHTML +=
              '<button type="button" class="dept__pdp-option-btn dept__pdp-variant-btn' + activeClasses + (isInitiallyActive ? ' dept__pdp-variant-btn--active' : '') + '"' +
                ' data-option-index="' + optionIndex + '"' +
                ' data-option-value="' + escapedValue + '">' +
                escapedValue +
              '</button>';
          }
        });

        optionsHTML += '</div></div>';
      });

      var accordionChevron = '<span class="dept__pdp-accordion__icon"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg></span>';

      function buildAccordion(title, content) {
        return '<details class="dept__pdp-accordion">' +
          '<summary class="dept__pdp-accordion__summary">' +
            '<span class="dept__pdp-accordion__title">' + escapeHTML(title) + '</span>' +
            accordionChevron +
          '</summary>' +
          '<div class="dept__pdp-accordion__content-wrapper">' +
            '<div class="dept__pdp-accordion__content">' +
              '<div class="dept__pdp-accordion__content-inner">' + content + '</div>' +
            '</div>' +
          '</div>' +
        '</details>';
      }

      var descriptionHTML = product.body_html ? buildAccordion('Product Details', product.body_html) : '';
      var productHandle = String(product.handle || '').trim();
      var accordionData = productHandle && accordionContentCache.hasOwnProperty(productHandle)
        ? accordionContentCache[productHandle]
        : null;
      var fabricAndCareContent = '';
      if (accordionData && typeof accordionData.fabric === 'string') {
        fabricAndCareContent = accordionData.fabric.trim();
      }
      if (!fabricAndCareContent && typeof product.fabric_html === 'string') {
        fabricAndCareContent = product.fabric_html.trim();
      }
      if (fabricAndCareContent) {
        descriptionHTML += buildAccordion('Fabric & Care', fabricAndCareContent);
      }
      descriptionHTML += buildAccordion('Care Tips', '<p>Turn garment inside out before wash to preserve finish.</p><p>Avoid bleach and high heat drying. Hang immediately after cycle for best drape.</p>');
      descriptionHTML += buildAccordion('Shipping & Returns', '<p>Free shipping on orders over $65. Returns accepted within 30 days of delivery.</p>');

      var atcDisabled = !productAvailable || !deptIsVariantAvailable(firstVariant);
      var atcButtonLabel = atcDisabled ? 'Sold Out' : 'Add to Cart';

      var pdpContentHTML =
        '<div class="dept__pdp-mount" data-pdp-mount>' +
        '<div class="dept__loading-skeleton pdp dept__pdp-skeleton-layer">' +
             '<div class="dept__skeleton-gallery">' +
               '<div class="dept__skeleton-thumbs">' +
                 '<div class="dept__skeleton-thumb"></div>' +
                 '<div class="dept__skeleton-thumb"></div>' +
                 '<div class="dept__skeleton-thumb"></div>' +
                 '<div class="dept__skeleton-thumb"></div>' +
               '</div>' +
               '<div class="dept__skeleton-main-img"></div>' +
             '</div>' +
             '<div class="dept__skeleton-info">' +
               '<div class="dept__skeleton-title"></div>' +
               '<div class="dept__skeleton-rating"></div>' +
               '<div class="dept__skeleton-price"></div>' +
               '<div class="dept__skeleton-swatches"></div>' +
               '<div class="dept__skeleton-options"></div>' +
               '<div class="dept__skeleton-options dept__skeleton-options--short"></div>' +
               '<div class="dept__skeleton-size-guide"></div>' +
               '<div class="dept__skeleton-btn"></div>' +
               '<div class="dept__skeleton-badges"></div>' +
               '<div class="dept__skeleton-accordion"></div>' +
               '<div class="dept__skeleton-accordion"></div>' +
               '<div class="dept__skeleton-accordion"></div>' +
               '<div class="dept__skeleton-accordion"></div>' +
             '</div>' +
             '</div>' +
        '<div class="dept__pdp-inner dept__pdp-content-layer dept__pdp-inner--pending" data-pdp-content>' +
          '<div class="dept__pdp-gallery">' +
            '<div class="dept__pdp-thumbs">' + thumbsHTML + '</div>' +
            '<div class="dept__pdp-main-image" data-main-image>' + allMediaHTML + pdpColorTagHTML + '</div>' +
          '</div>' +
          '<div class="dept__pdp-info">' +
            buildSoldBadgeHTML(product) +
            '<h2 class="dept__pdp-title"><a href="/products/' + encodeURIComponent(product.handle) + '" class="dept__pdp-title-link">' + escapeHTML(product.title) + '</a></h2>' +
            buildRatingHTML() +
            '<div class="dept__pdp-price">' +
              '<span data-pdp-price>' + price + '</span>' +
              '<span data-pdp-compare>' + comparePriceHTML + '</span>' +
            '</div>' +
            colorSwitcherHTML +
            optionsHTML +
            '<button type="button" class="dept__pdp-size-chart-btn" data-dept-size-chart-open>' +
              'Size Chart' +
            '</button>' +
            '<button type="button" class="dept__pdp-atc" data-variant-id="' + ((firstVariant && firstVariant.id) || '') + '"' + (atcDisabled ? ' disabled' : '') + '>' + atcButtonLabel + '</button>' +
            '<div class="dept__pdp-badges">' +
              '<div class="dept__pdp-badge">' +
                '<svg class="dept__pdp-badge-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><text x="12" y="15" text-anchor="middle" font-size="8" font-weight="700" fill="currentColor" stroke="none">30</text></svg>' +
                '<div class="dept__pdp-badge-text"><span class="dept__pdp-badge-title">30-Day Guarantee</span><span class="dept__pdp-badge-sub">Easy Returns &amp; Exchanges</span></div>' +
              '</div>' +
            '</div>' +
            descriptionHTML +
          '</div>' +
        '</div>' +
        '</div>';

      pdpArea.innerHTML = pdpContentHTML;
      pdpArea.dataset.currentProductHandle = product.handle || '';
      bindPDPInteractions(product, mediaList, variants, selectedByIndex);
      var skeleton = pdpArea.querySelector('.dept__pdp-skeleton-layer');
      if (skeleton && skeleton.parentNode) skeleton.remove();
      var content = pdpArea.querySelector('[data-pdp-content]');
      if (content) {
        content.classList.remove('dept__pdp-inner--pending');
        content.classList.add('is-ready');
      }

      hydrateRating(product);
    }

    function fetchProductJsonCached(handle, options) {
      options = options || {};
      var normalizedHandle = String(handle || '').trim();
      if (!normalizedHandle) return Promise.resolve(null);
      if (productJsonCache[normalizedHandle]) {
        return Promise.resolve(productJsonCache[normalizedHandle]);
      }
      if (productJsonFetchCache[normalizedHandle]) {
        return productJsonFetchCache[normalizedHandle];
      }

      productJsonFetchCache[normalizedHandle] = fetch('/products/' + encodeURIComponent(normalizedHandle) + '.json')
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          var product = data && data.product ? data.product : null;
          if (product) {
            var productHandle = String(product.handle || normalizedHandle).trim();
            if (productHandle) {
              productJsonCache[productHandle] = product;
            }
            productJsonCache[normalizedHandle] = product;
          }
          return product;
        })
        .catch(function (error) {
          if (!options.prefetch) throw error;
          return null;
        })
        .then(function (product) {
          delete productJsonFetchCache[normalizedHandle];
          return product;
        }, function (error) {
          delete productJsonFetchCache[normalizedHandle];
          throw error;
        });

      return productJsonFetchCache[normalizedHandle];
    }

    function pdpErrorHTML() {
      return '<p style="color:#a1a1aa;padding:16px;">Could not load product details.</p>';
    }

    // For automatic/code sales the storefront variant price is the FULL price
    // (discount applies at checkout). Return a SHALLOW-CLONED product whose
    // variants carry price = discounted, compare_at_price = original — so the
    // discovery renderer (which strikes compare_at over price) shows the deal
    // price + struck regular with NO renderer changes. The cache stays pristine
    // (we clone), so re-opening never double-discounts. price_edit/unknown is
    // returned untouched (Shopify's own price/compare_at already reflect it).
    function applyDealToProduct(product, deal) {
      var sale = deal && deal._sale;
      var mech = sale && sale.mechanism;
      if (!product || !Array.isArray(product.variants) ||
          (mech !== 'automatic' && mech !== 'code')) return product;
      var clone = {};
      for (var k in product) { if (Object.prototype.hasOwnProperty.call(product, k)) clone[k] = product[k]; }
      clone.variants = product.variants.map(function (v) {
        var fullCents = normalizeMoneyInput(v.price).cents;
        var dealCents = applyDiscountCents(fullCents, sale.value);
        // Apply whenever the deal actually reduces the price (incl. all the way to
        // $0 for a 100%/large-fixed sale) — matches the card, which renders any
        // dealCents < full. Only a no-op (deal >= full, or $0 base) is left as-is.
        if (!(dealCents < fullCents)) return v;
        // Struck "regular" = the higher of a genuine existing markdown and the full
        // price — identical to the card's computeDealPricing, so the two agree.
        var existingCompareCents = v.compare_at_price ? normalizeMoneyInput(v.compare_at_price).cents : 0;
        var regularCents = (existingCompareCents > fullCents) ? existingCompareCents : fullCents;
        var nv = {};
        for (var k2 in v) { if (Object.prototype.hasOwnProperty.call(v, k2)) nv[k2] = v[k2]; }
        nv.compare_at_price = (regularCents / 100).toFixed(2);
        nv.price = (dealCents / 100).toFixed(2);
        return nv;
      });
      return clone;
    }

    // Surface the discount code under the price for code-mechanism sales only.
    function injectDealCode(deal) {
      var sale = deal && deal._sale;
      if (!sale || sale.mechanism !== 'code') return;
      var code = String((deal._code || sale.discountCode || '')).trim();
      if (!code) return;
      var priceEl = pdpArea.querySelector('.dept__pdp-price');
      if (!priceEl || pdpArea.querySelector('.flash-deals__pdp-code')) return;
      var el = document.createElement('div');
      el.className = 'flash-deals__pdp-code';
      el.innerHTML = 'Use code <strong>' + escapeHTML(code) + '</strong> at checkout';
      priceEl.insertAdjacentElement('afterend', el);
    }

    // Entry point — mirrors the discovery WARM path: structure from the .json
    // product, per-variant availability merged in from the .js endpoint (.json
    // omits it), and the product videos resolved up front so renderPDP builds the
    // video as media[0] WITH its thumbnail — exactly like the discovery section.
    // `deal` carries the colours-manager sale so prices reflect the active deal.
    function open(handle, deal) {
      clearPDP();
      pdpArea.innerHTML = loadingPDPHTML();
      var token = ++latestPdpRequestToken;
      var pProduct = fetchProductJsonCached(handle).catch(function () { return null; });
      var pCtx = (window.HealixColors && typeof window.HealixColors.product === 'function')
        ? window.HealixColors.product(handle).catch(function () { return null; })
        : Promise.resolve(null);
      var pVideos = fetchProductVideosPromise(handle).catch(function () { return []; });
      Promise.all([pProduct, pCtx, pVideos]).then(function (res) {
        if (token !== latestPdpRequestToken) return;
        var product = applyDealToProduct(res[0], deal);
        var ctx = res[1] ? { variants: (res[1].variants || []) } : null;
        var videos = Array.isArray(res[2]) ? res[2] : [];
        if (!product) { pdpArea.innerHTML = pdpErrorHTML(); return; }
        renderPDP(product, ctx, videos);
        injectDealCode(deal);
      }).catch(function () {
        if (token !== latestPdpRequestToken) return;
        pdpArea.innerHTML = pdpErrorHTML();
      });
    }

    // Warm the exact caches open() reads from (.json structure + product-page
    // HTML for videos), so a later open() is an instant cache hit. Deduped by the
    // underlying *Cached helpers, so repeat calls are cheap. Returns a promise so
    // the caller can pace concurrency.
    function prefetch(handle) {
      if (!handle) return Promise.resolve();
      return Promise.all([
        fetchProductJsonCached(handle).catch(function () { return null; }),
        fetchProductVideosPromise(handle).catch(function () { return []; })
      ]);
    }

    return { open: open, clear: clearPDP, prefetch: prefetch };
  }

  // ── Per-section init ──────────────────────────────────────────────────────
  function initSection(root) {
    if (!root || root.dataset.flashDealsInitialized === 'true') return;
    root.dataset.flashDealsInitialized = 'true';

    var bodyEl = root.querySelector('[data-flash-deals-body]');
    var pdpArea = root.querySelector('[data-flash-deals-pdp]');
    var filtersEl = root.querySelector('[data-flash-deals-filters]');
    if (!bodyEl || !pdpArea) return;

    var deptPDP = createDiscoveryPDP(pdpArea);

    // Background mini-PDP prefetch. Warms each deal's PDP caches during idle time
    // with a small concurrency cap so opening any card is instant, without
    // hammering the storefront. Skipped on data-saver / 2G to respect the user.
    var _pdpPrefetchStarted = false;
    function prefetchPDPsInBackground(handles) {
      if (_pdpPrefetchStarted || !deptPDP || !handles || !handles.length) return;
      _pdpPrefetchStarted = true;
      try {
        var c = navigator.connection;
        if (c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || ''))) return;
      } catch (e) {}
      var queue = handles.filter(Boolean);
      var MAX = 3, active = 0, i = 0;
      function pump() {
        while (active < MAX && i < queue.length) {
          active++;
          deptPDP.prefetch(queue[i++]).then(done, done);
        }
      }
      function done() { active--; pump(); }
      var start = function () { pump(); };
      if (window.requestIdleCallback) window.requestIdleCallback(start, { timeout: 2500 });
      else setTimeout(start, 800);
    }

    var showEmpty = root.dataset.showEmpty !== 'false';
    var emptyText = root.dataset.emptyText || 'No flash deals are live right now.';
    var saleMap = {};
    var dealIndex = {}; // handle -> deal meta (scoped to this section)

    initCountdown(root);

    // Delegates rendering to the ported discovery mini-PDP (1:1 with the home page).
    function openPDP(handle, cardEl, noScroll) {
      root.querySelectorAll('.flash-deals__card.is-active').forEach(function (el) { el.classList.remove('is-active'); });
      if (cardEl) cardEl.classList.add('is-active');
      if (!noScroll) smoothScrollToElement(pdpArea);
      deptPDP.open(handle, dealIndex[handle]);
    }

    function cardFromEvent(e) {
      var el = e.target && e.target.closest ? e.target.closest('[data-flash-deals-card]') : null;
      return el && root.contains(el) ? el : null;
    }

    // Cards contain native <button>s (image + claim) which fire click on mouse
    // and keyboard (Enter/Space) and bubble here — one handler covers both.
    bodyEl.addEventListener('click', function (e) {
      var cardEl = cardFromEvent(e);
      if (!cardEl) return;
      e.preventDefault();
      openPDP(cardEl.getAttribute('data-handle'), cardEl);
    });

    // Stateful gender + category filtering. Cards carry data-deals-gender and
    // data-deals-category; both must match the active selection to stay visible.
    var gendersEl = root.querySelector('[data-flash-deals-genders]');
    var currentGender = '';
    var currentCategory = '';
    var allDealsRef = [];

    function applyFilters() {
      bodyEl.querySelectorAll('[data-flash-deals-card]').forEach(function (card) {
        var g = card.getAttribute('data-deals-gender') || '';
        var c = card.getAttribute('data-deals-category') || '';
        var genderOk = !currentGender || !g || g === currentGender; // shared (no gender) shows in both
        var catOk = !currentCategory || c === currentCategory;
        card.hidden = !(genderOk && catOk);
      });
    }

    // Rebuild category pills for the active gender (deals with no gender are shared),
    // resetting to "All". Keeps category tabs scoped to the chosen gender.
    function refreshCategories() {
      if (!filtersEl) return;
      var seen = {};
      var types = [];
      allDealsRef.forEach(function (d) {
        if (!d._category) return;
        if (currentGender && d._gender && d._gender !== currentGender) return;
        var k = handleizeJS(d._category);
        if (!seen[k]) { seen[k] = true; types.push(d._category); }
      });
      currentCategory = '';
      if (types.length) buildPills(filtersEl, types);
      else filtersEl.innerHTML = '';
      updateScrollIndicator();
      if (window.requestAnimationFrame) window.requestAnimationFrame(updateScrollIndicator);
    }

    // ── Horizontal scroll indicator under the category pills ──────────────────
    // A thin progress bar that appears ONLY when the pills overflow their row
    // (i.e. on mobile where they scroll horizontally; on desktop they wrap, so
    // it stays hidden). The thumb width = visible fraction; its position tracks
    // scrollLeft so the user can see there's more and how far they've scrolled.
    var scrollIndicator = null, scrollThumb = null;
    if (filtersEl) {
      scrollIndicator = document.createElement('div');
      scrollIndicator.className = 'flash-deals__scroll-indicator';
      scrollIndicator.setAttribute('data-fd-scroll-indicator', '');
      scrollIndicator.hidden = true;
      scrollThumb = document.createElement('div');
      scrollThumb.className = 'flash-deals__scroll-indicator-thumb';
      scrollIndicator.appendChild(scrollThumb);
      filtersEl.insertAdjacentElement('afterend', scrollIndicator);
    }
    function updateScrollIndicator() {
      if (!filtersEl || !scrollIndicator || !scrollThumb) return;
      var sw = filtersEl.scrollWidth, cw = filtersEl.clientWidth;
      var scrollable = (sw - cw) > 2;
      scrollIndicator.hidden = !scrollable;
      if (!scrollable) return;
      var thumbW = Math.max(Math.min((cw / sw) * 100, 22), 8); // % of track: small handle, 8–22%
      var maxScroll = sw - cw;
      var prog = maxScroll > 0 ? (filtersEl.scrollLeft / maxScroll) : 0;
      scrollThumb.style.width = thumbW + '%';
      scrollThumb.style.left = (prog * (100 - thumbW)) + '%';
    }
    if (filtersEl) {
      filtersEl.addEventListener('scroll', updateScrollIndicator, { passive: true });
      window.addEventListener('resize', updateScrollIndicator);
    }

    if (filtersEl) {
      filtersEl.addEventListener('click', function (e) {
        var btn = e.target && e.target.closest ? e.target.closest('[data-fd-pill]') : null;
        if (!btn || !filtersEl.contains(btn)) return;
        currentCategory = btn.getAttribute('data-fd-pill') || '';
        filtersEl.querySelectorAll('.flash-deals__pill').forEach(function (b) {
          var active = b === btn;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        applyFilters();
      });
    }

    if (gendersEl) {
      gendersEl.addEventListener('click', function (e) {
        var btn = e.target && e.target.closest ? e.target.closest('[data-fd-gender]') : null;
        if (!btn || !gendersEl.contains(btn)) return;
        currentGender = btn.getAttribute('data-fd-gender') || '';
        gendersEl.querySelectorAll('.flash-deals__gender').forEach(function (b) {
          var active = b === btn;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        refreshCategories(); // pills scoped to the chosen gender, reset to All
        applyFilters();
      });
    }

    if (!window.HealixColors || typeof window.HealixColors.deals !== 'function') {
      renderEmpty(bodyEl, emptyText, showEmpty);
      return;
    }

    renderSkeleton(bodyEl);

    window.HealixColors.deals().then(function (data) {
      var groups = (data && Array.isArray(data.byColor)) ? data.byColor : [];
      saleMap = buildSaleMap(data && data.sales);

      // Flatten all colour groups into a single de-duplicated deal list.
      var seen = {};
      var allDeals = [];
      groups.forEach(function (grp) {
        (Array.isArray(grp.products) ? grp.products : []).forEach(function (p) {
          if (!p || !p.handle || seen[p.handle]) return;
          seen[p.handle] = true;
          var deal = {
            handle: p.handle, title: p.title, discountLabel: p.discountLabel, saleId: p.saleId,
            discountCode: p.discountCode,
            color: p.color || grp.color, colorName: p.colorName || grp.colorName,
            hex: p.hex || grp.hex, edition: p.edition || grp.edition
          };
          // Resolve the structured sale (mechanism + value + code) by saleId. This
          // is the SOLE basis for pricing — the discountLabel string is display-only.
          deal._sale = saleMap[p.saleId] || null;
          deal._code = getDiscountCode(deal, saleMap);
          deal._family = familyLabel(deal);
          dealIndex[p.handle] = deal;
          allDeals.push(deal);
        });
      });

      if (!allDeals.length) { renderEmpty(bodyEl, emptyText, showEmpty); return; }

      // Category = Shopify product type (the coarse Tops/Bottoms/Jumpsuits/Accessories
      // grouping). It is NOT in the deals payload, so we read it from the enriched
      // product (/products/{handle}.js → product.type). HealixColors.product caches,
      // so this pre-fetch and enrichCard's fetch share one request per product.
      Promise.all(allDeals.map(function (deal) {
        return window.HealixColors.product(deal.handle).then(function (p) {
          deal._product = p || null;
          deal._category = (p && p.type) ? String(p.type).trim() : '';
          deal._gender = genderForDeal(deal);
          return deal;
        }).catch(function () {
          deal._category = '';
          deal._gender = genderFromHandle(deal.handle); // handle works even if enrichment fails
          return deal;
        });
      })).then(function () {
        // Stock rule: a card shows if ANY variant is in stock; the per-variant
        // sold-out state then appears in the mini-PDP when opened. Products whose
        // EVERY variant is sold out are dropped entirely (no card). Products we
        // couldn't resolve (enrichment failed, no _product) are kept.
        allDeals = allDeals.filter(function (deal) {
          if (!deal._product) return true;
          return !!findFirstAvailableVariant(deal._product.variants);
        });
        if (!allDeals.length) { renderEmpty(bodyEl, emptyText, showEmpty); return; }
        allDealsRef = allDeals;

        // Gender presence — entirely data-driven from the products the API returned.
        var mensProducts = allDeals.filter(function (d) { return d._gender === 'men'; });
        var womensProducts = allDeals.filter(function (d) { return d._gender === 'women'; });
        var hasMensProducts = mensProducts.length > 0;
        var hasWomensProducts = womensProducts.length > 0;
        var bothGenders = hasMensProducts && hasWomensProducts;

        console.log('Mens products:', mensProducts.length);
        console.log('Womens products:', womensProducts.length);
        console.log('Show gender switcher:', bothGenders);

        // Gender switcher ONLY when BOTH genders exist (same availability rule as
        // the collection template). Otherwise hide it and show categories directly.
        if (gendersEl) {
          if (bothGenders) {
            gendersEl.hidden = false;
            gendersEl.innerHTML =
              '<button type="button" class="flash-deals__gender is-active" data-fd-gender="men" aria-pressed="true">Men</button>' +
              '<button type="button" class="flash-deals__gender" data-fd-gender="women" aria-pressed="false">Women</button>';
            currentGender = 'men';
          } else {
            gendersEl.hidden = true;
            gendersEl.innerHTML = '';
            currentGender = (hasWomensProducts && !hasMensProducts) ? 'women'
              : (hasMensProducts ? 'men' : '');
          }
        }

        // Build the grid; cards carry data-deals-gender + data-deals-category.
        var grid = document.createElement('div');
        grid.className = 'flash-deals__grid';
        allDeals.forEach(function (deal) {
          var card = buildCard(deal);
          grid.appendChild(card);
          enrichCard(card, deal);
        });

        bodyEl.innerHTML = '';
        bodyEl.appendChild(grid);
        bodyEl.setAttribute('aria-busy', 'false');

        // Category pills scoped to the active gender, then apply the initial filter.
        refreshCategories();
        applyFilters();

        // Default selection: auto-open the first visible deal's mini-PDP. noScroll
        // so the page does not jump down to the PDP on initial load.
        var firstCard = bodyEl.querySelector('[data-flash-deals-card]:not([hidden])');
        if (firstCard) openPDP(firstCard.getAttribute('data-handle'), firstCard, true);

        // Warm every deal's mini-PDP in the background so opening a card is instant.
        prefetchPDPsInBackground(allDealsRef.map(function (d) { return d.handle; }));
      });
    }).catch(function () {
      renderEmpty(bodyEl, emptyText, showEmpty);
    });
  }

  function initAll() {
    document.querySelectorAll('[data-flash-deals]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor lifecycle: (re)init when the section is added/reloaded.
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target && e.target.querySelector ? e.target.querySelector('[data-flash-deals]') : null;
    if (el) initSection(el);
  });
})();
